import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  effect,
  OnInit,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GestionarContactosInformeDialogComponent } from 'src/app/presentation/shared/contactos/gestionar-contactos-informe-dialog.component';
import { gestionContactosInformeSiAplica } from 'src/app/presentation/shared/contactos/contactos-informe.utils';
import { avisarPostPersistencia } from 'src/app/common/aviso-post-persistencia';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';

import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import { DocumentoP8Service } from 'src/app/domain/service/documento-p8/documento-p8.service';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import { BloqueoProgramacionRepository } from 'src/app/domain/service/bloqueo-programacion/bloqueo-programacion-repository';
import {
  OrdenServicio,
  OrdenServicioDetalleProgramar,
  ProgramacionServicio,
  TareaBeneficiario,
  AsesorSugerido
} from 'src/app/domain/model/orden-servicio/orden-servicio';
import { PAGE_SIZE_MAX } from 'src/app/common/validation-utils';
import {
  esPersonaNoEncontrada,
  MENSAJE_PERSONA_DESACTUALIZADA
} from 'src/app/common/http-error-utils';
import {
  CancelarCitaDialogComponent,
  CancelarCitaResult,
  DatosPersona
} from 'src/app/presentation/agenda/cita/components/cancelar-cita-dialog.component';
import {
  ContactosTrabajoDialogComponent,
  ContactoCentroTrabajo,
  AccionPaginacionContactos
} from 'src/app/presentation/agenda/cita/components/contactos-trabajo-dialog.component';
import { ContactosCursorPager } from 'src/app/presentation/shared/contactos/contactos-cursor-pager';
import { HistorialGestionesDialogComponent } from 'src/app/presentation/gestion-tarea/historial-gestiones-dialog.component';
import {
  IngresarGestionDialogComponent,
  IngresarGestionResult
} from 'src/app/presentation/gestion-tarea/ingresar-gestion-dialog.component';
import { SeleccionarAsesorDialogComponent } from './seleccionar-asesor-dialog.component';
import { AgendaAsesorDialogComponent } from './agenda-asesor-dialog.component';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { environment } from 'src/environments/environment';
import { ContactoPersonaRepository } from 'src/app/domain/service/persona/contacto-persona-repository';
import {
  getTipoIdentificacionDescripcion,
  ESTADO_CITA
} from 'src/app/common/constants-ejb';
import {
  sanitizeText,
  sanitizeEmail,
  emailKeypress,
  isValidEmail,
  isValidEmailContactoLegacy
} from 'src/app/common/input-utils';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import {
  ProgramacionPendiente,
  CumplimientoRequest,
  HistorialCumplimiento,
  DatosActaRequest
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import { FormularioCumplimientoComponent } from 'src/app/presentation/cumplimientos/components/formulario-cumplimiento/formulario-cumplimiento.component';
import { HistorialCumplimientosDialogComponent } from 'src/app/presentation/cumplimientos/components/historial-cumplimientos-dialog/historial-cumplimientos-dialog.component';
import { MunicipioPrecargado } from 'src/app/presentation/cumplimientos/components/seccion-transporte/seccion-transporte.component';
import { abrirAlertaTemprana } from 'src/app/common/alerta-temprana.utils';
import {
  esTipoCitaCampo,
  esTipoCitaInforme
} from 'src/app/common/tipo-cita.utils';
import {
  programarServicioProgramacionesPreset,
  programarServicioTareasPreset
} from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * ProgramarServicioDialogComponent
 *
 * Diálogo para programar un servicio (cita).
 * Replica la funcionalidad del legacy ProgramarServiciosViewImpl.java
 *
 * Estructura:
 * - Panel detalle de la orden (ServiceDetailsInterfaceImpl)
 * - Tabla de programaciones existentes
 * - Tabla de otras tareas del beneficiario
 * - Acciones: Programar Cita, Historial Llamadas, Contactos
 *
 * @author GIROP Migration
 * @since 1.0
 */
@Component({
  selector: 'app-programar-servicio-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    GestionarContactosInformeDialogComponent,
    ButtonModule,
    FieldsetModule,
    TableModule,
    TagModule,
    TooltipModule,
    SkeletonModule,
    DividerModule,
    ToastModule,
    InputTextModule,
    CancelarCitaDialogComponent,
    HistorialGestionesDialogComponent,
    ContactosTrabajoDialogComponent,
    ProgressSpinnerModule,
    CheckboxModule,
    IngresarGestionDialogComponent,
    SeleccionarAsesorDialogComponent,
    AgendaAsesorDialogComponent,
    FormularioCumplimientoComponent,
    HistorialCumplimientosDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>

    <!-- Overlay de procesamiento para Descargar Acta -->
    @if (descargandoActa()) {
      <div
        class="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center"
      >
        <div
          class="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-4"
        >
          <p-progressSpinner
            styleClass="w-16 h-16"
            strokeWidth="4"
            animationDuration=".8s"
          >
          </p-progressSpinner>
          <span class="text-lg font-medium text-gray-700"
            >Descargando acta...</span
          >
          <span class="text-sm text-gray-500">Por favor espere</span>
        </div>
      </div>
    }

    <!-- Overlay de procesamiento para Cancelar Programación -->
    @if (cancelandoProgramacion()) {
      <div
        class="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center"
      >
        <div
          class="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-4"
        >
          <p-progressSpinner
            styleClass="w-16 h-16"
            strokeWidth="4"
            animationDuration=".8s"
          >
          </p-progressSpinner>
          <span class="text-lg font-medium text-gray-700"
            >Cancelando cita...</span
          >
          <span class="text-sm text-gray-500">Por favor espere</span>
        </div>
      </div>
    }

    <p-dialog
      [header]="
        'Programar Servicio - Orden ' +
        (detalle()?.codigoOrden ||
          ordenSeleccionada()?.nmconsecutivoManualOrden ||
          '')
      "
      [visible]="visible()"
      [modal]="true"
      [closable]="!cargando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '95vw', maxWidth: '1400px' }"
      [contentStyle]="{ 'max-height': '85vh', overflow: 'auto' }"
      (visibleChange)="onDialogVisibleChange($event)"
    >
      @if (cargando()) {
        <!-- Skeleton de carga -->
        <div class="p-4">
          <p-skeleton
            width="100%"
            height="200px"
            styleClass="mb-4"
          ></p-skeleton>
          <p-skeleton width="100%" height="150px"></p-skeleton>
        </div>
      } @else if (detalle()) {
        <!-- Contenido del diálogo -->
        <div class="flex flex-col gap-4">
          <!-- === Panel Detalle del Servicio === -->
          <p-fieldset legend="Detalle del Servicio" [toggleable]="true">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Aseguradora (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block"
                  >1. Aseguradora</label
                >
                <span class="font-medium">{{
                  nombreAseguradora() || detalle()?.aseguradora || '-'
                }}</span>
              </div>

              <!-- Cliente (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block">2. Cliente</label>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="font-medium">{{
                    detalle()?.nombreBeneficiario || '-'
                  }}</span>
                  <span
                    class="text-sm text-gray-500"
                    *ngIf="detalle()?.dniBeneficiario"
                  >
                    ({{ detalle()?.dniBeneficiario }})
                  </span>
                  <button
                    pButton
                    icon="pi pi-building"
                    class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                    [pTooltip]="'Información Empresa'"
                    (click)="abrirInformacionEmpresaDesdeDetalle()"
                  ></button>
                </div>
              </div>

              <!-- Orden, Unidad Administrativa, Clasificación -->
              <div>
                <label class="text-xs text-gray-500 block">3. Orden</label>
                <span class="font-medium">{{
                  detalle()?.codigoOrden || '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >4. Unidad Administrativa</label
                >
                <span class="font-medium">{{
                  detalle()?.unidadEstrategica || detalle()?.cduen || '-'
                }}</span>
              </div>
              <div class="lg:col-span-2">
                <label class="text-xs text-gray-500 block"
                  >5. Clasificación</label
                >
                <span class="font-medium">{{
                  detalle()?.clasificacion || '-'
                }}</span>
              </div>

              <!-- Producto (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block">6. Producto</label>
                <span class="font-medium">{{
                  detalle()?.producto || '-'
                }}</span>
              </div>

              <!-- Tarea (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block">7. Tarea</label>
                <span class="font-medium">{{
                  detalle()?.tareaCliente || '-'
                }}</span>
              </div>

              <!-- Servicio CGR (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block"
                  >8. Servicio CGR</label
                >
                <span class="font-medium">{{
                  detalle()?.servicioCgr || '-'
                }}</span>
              </div>

              <!-- Requiere Informe -->
              <div>
                <label class="text-xs text-gray-500 block"
                  >9. Requiere Informe</label
                >
                @if (detalle()?.requiereInforme === 'S') {
                  <p-tag severity="info" value="Sí"></p-tag>
                } @else {
                  <span class="text-gray-500">No</span>
                }
              </div>

              <!-- Fila 4: Fechas de Entrega, Cantidades -->
              <div>
                <label class="text-xs text-gray-500 block"
                  >10. Fecha Inicial Entrega</label
                >
                <span class="font-medium">{{
                  formatDate(detalle()?.fechaEntregaInicial)
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >11. Fecha Final Entrega</label
                >
                <span class="font-medium">{{
                  formatDate(detalle()?.fechaEntregaFinal)
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >12. Cantidad Pedida</label
                >
                <span class="font-medium">{{
                  detalle()?.cantidadPedida || 0
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >13. Cantidad Programada</label
                >
                <span class="font-medium">{{
                  detalle()?.cantidadProgramada || 0
                }}</span>
              </div>

              <!-- Fila 5: Saldos -->
              <div>
                <label class="text-xs text-gray-500 block"
                  >14. Saldo por Programar</label
                >
                <span
                  class="font-bold"
                  [class.text-red-600]="(detalle()?.saldoPorProgramar || 0) > 0"
                >
                  {{ detalle()?.saldoPorProgramar || 0 }}
                </span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >15. Saldo por Ejecutar</label
                >
                <span
                  class="font-medium"
                  [class.text-orange-600]="
                    (detalle()?.saldoPorEjecutar || 0) > 0
                  "
                >
                  {{ detalle()?.saldoPorEjecutar || 0 }}
                </span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block">Prioridad</label>
                @if (detalle()?.prioridad === 'S') {
                  <p-tag severity="danger" value="ALTA"></p-tag>
                } @else {
                  <span class="text-gray-500">Normal</span>
                }
              </div>
              <div></div>

              <!-- Estado de la Tarea (línea completa) -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block"
                  >16. Estado de la Tarea</label
                >
                <p-tag
                  [value]="detalle()?.estado || 'Sin estado'"
                  [severity]="getEstadoSeverity(detalle()?.cdestado)"
                ></p-tag>
              </div>

              <!-- Fila 6: Profesional, Consultor -->
              <div>
                <label class="text-xs text-gray-500 block"
                  >17. Profesional</label
                >
                <span class="font-medium">{{
                  detalle()?.profesional || '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block">18. Consultor</label>
                <span class="font-medium">{{
                  detalle()?.asesorSugerido || '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >19. Nombre Contacto</label
                >
                <span class="font-medium">{{
                  detalle()?.nombreContacto || '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >20. Teléfono Contacto</label
                >
                <span class="font-medium">{{
                  detalle()?.telefonoContacto || '-'
                }}</span>
              </div>

              <!-- Fila 7: Dirección, Municipio, Centro de Trabajo -->
              <div class="lg:col-span-2">
                <label class="text-xs text-gray-500 block"
                  >21. Dirección de Entrega</label
                >
                <span class="font-medium">{{
                  detalle()?.direccionEntrega ||
                    ordenSeleccionada()?.dsdireccion ||
                    '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >22. Municipio de Entrega</label
                >
                <span class="font-medium">{{
                  detalle()?.municipioDestino || '-'
                }}</span>
              </div>
              <div>
                <label class="text-xs text-gray-500 block"
                  >23. Centro de Trabajo</label
                >
                <span class="font-medium">{{
                  detalle()?.centroTrabajo ||
                    ordenSeleccionada()?.dscentroTrabajo ||
                    '-'
                }}</span>
              </div>

              <!-- Fila 8: Localidad, Barrio -->
              <div class="lg:col-span-2">
                <label class="text-xs text-gray-500 block">24. Localidad</label>
                <span class="font-medium">{{
                  detalle()?.localidad ||
                    ordenSeleccionada()?.dslocalidadPpal ||
                    '-'
                }}</span>
              </div>
              <div class="lg:col-span-2">
                <label class="text-xs text-gray-500 block">25. Barrio</label>
                <span class="font-medium">{{
                  detalle()?.barrio || ordenSeleccionada()?.dsbarrioPpal || '-'
                }}</span>
              </div>

              <!-- Fila 9: Observaciones -->
              <div class="col-span-full">
                <label class="text-xs text-gray-500 block"
                  >26. Observaciones</label
                >
                <p class="text-sm mt-1 whitespace-pre-wrap">
                  {{ detalle()?.notas || '-' }}
                </p>
              </div>
            </div>
          </p-fieldset>

          <!-- === Tabla de Programaciones Existentes === -->
          <p-fieldset legend="Programaciones de esta Orden" [toggleable]="true">
            @if (cargandoProgramaciones()) {
              <p-skeleton width="100%" height="100px"></p-skeleton>
            } @else if (programaciones().length === 0) {
              <div class="text-center py-4 text-gray-500">
                <i class="pi pi-calendar-times text-2xl mb-2"></i>
                <p>No hay programaciones registradas para esta orden</p>
              </div>
            } @else {
              <div class="md:hidden space-y-3 mb-3">
                @for (
                  prog of programacionesMobilePage();
                  track prog.nmconsecutivo ?? $index
                ) {
                  <article
                    class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
                  >
                    <div class="flex items-start justify-between gap-2 mb-2">
                      <h3
                        class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                      >
                        Programación {{ textoTabla(prog.nmconsecutivo) }}
                      </h3>
                      <span class="text-xs text-gray-500 dark:text-gray-400">{{
                        textoTabla(formatDateTime(prog.fechaProgramacion))
                      }}</span>
                    </div>

                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3"
                    >
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Tipo:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(prog.dstipoCita)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Consultor:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(prog.nombreAsesor)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Duración:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla((prog.horasCita || 0) + 'h')
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Cantidad:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(prog.cantidadProgramada || 0)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Estado Cita:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(prog.estado || prog.cdestado || '-')
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Estado Acta:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(prog.estadoActa)
                        }}</span>
                      </div>
                    </div>

                    <div class="flex flex-wrap gap-2">
                      @if (
                        prog.snpermiteCumplimiento === 'S' &&
                        !esCitaCancelada(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-check-circle"
                          class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                          pTooltip="Ingresar Cumplimiento"
                          (click)="ingresarCumplimiento(prog)"
                        ></button>
                      }
                      @if (
                        prog.snpermiteCancelacion === 'S' &&
                        !esCitaCancelada(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-times-circle"
                          class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                          pTooltip="Cancelar Programación"
                          (click)="cancelarProgramacion(prog)"
                        ></button>
                      }
                      @if (
                        prog.nmcumplimientoServicio && !esCitaCancelada(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-user-edit"
                          class="p-button-rounded p-button-text p-button-sm p-button-info min-w-[44px] min-h-[44px]"
                          pTooltip="Editar Contacto"
                          (click)="editarContacto(prog)"
                        ></button>
                      }
                      @if (
                        mostrarBotonAsociarActa(prog) &&
                        !esCitaCancelada(prog) &&
                        !esCitaEjecutada(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-file-edit"
                          class="p-button-rounded p-button-text p-button-sm p-button-warning min-w-[44px] min-h-[44px]"
                          pTooltip="Asociar Acta"
                          [disabled]="
                            debeDeshabilitarAsociarActa(prog) ||
                            loadingAsociarActa()
                          "
                          (click)="asociarActa(prog)"
                        ></button>
                      }
                      @if (
                        (esActaAsociada(prog) || esActaEnviadaA360(prog)) &&
                        !esCitaCancelada(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-refresh"
                          class="p-button-rounded p-button-text p-button-sm p-button-help min-w-[44px] min-h-[44px]"
                          pTooltip="Refrescar Estado"
                          [loading]="cargandoProgramaciones()"
                          (click)="refrescarProgramaciones()"
                        ></button>
                      }
                      @if (
                        tieneActaDescargable(prog) &&
                        !esCitaCancelada(prog) &&
                        !esActaEnviadaA360(prog)
                      ) {
                        <button
                          pButton
                          icon="pi pi-download"
                          class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                          pTooltip="Descargar Acta"
                          [disabled]="!!descargandoActa()"
                          (click)="descargarActa(prog)"
                        ></button>
                      }
                      <button
                        pButton
                        icon="pi pi-eye"
                        class="p-button-rounded p-button-text p-button-sm p-button-secondary min-w-[44px] min-h-[44px]"
                        pTooltip="Ver Observaciones"
                        [disabled]="!prog.dsobservaciones"
                        (click)="verObservaciones(prog)"
                      ></button>
                    </div>
                  </article>
                }

                <div class="flex items-center justify-between gap-2 pt-1">
                  <button
                    pButton
                    type="button"
                    label="Anterior"
                    icon="pi pi-angle-left"
                    class="p-button-text p-button-sm"
                    [disabled]="!canGoPrevProgramacionesMobile()"
                    (click)="prevProgramacionesMobilePage()"
                  ></button>

                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ programacionesMobileFirstRecord() }} -
                    {{ programacionesMobileLastRecord() }} de
                    {{ programaciones().length }}
                  </span>

                  <button
                    pButton
                    type="button"
                    label="Siguiente"
                    icon="pi pi-angle-right"
                    iconPos="right"
                    class="p-button-text p-button-sm"
                    [disabled]="!canGoNextProgramacionesMobile()"
                    (click)="nextProgramacionesMobilePage()"
                  ></button>
                </div>
              </div>

              <div class="hidden md:block">
                <div
                  role="region"
                  [attr.aria-label]="
                    programacionesTableConfig().a11y.tableAriaLabel
                  "
                  class="sr-only"
                ></div>
                <p-table
                  [resizableColumns]="true"
                  columnResizeMode="expand"
                  [value]="programaciones()"
                  [scrollable]="true"
                  [responsiveLayout]="'scroll'"
                  [tableStyle]="{
                    'min-width':
                      programacionesTableConfig().responsive.minWidth || '92rem'
                  }"
                  [paginator]="programacionesTableConfig().pagination.enabled"
                  [rows]="programacionesTableConfig().pagination.pageSize"
                  [rowsPerPageOptions]="
                    programacionesTableConfig().pagination.pageSizeOptions
                  "
                  [showCurrentPageReport]="true"
                  [currentPageReportTemplate]="
                    programacionesTableConfig().pagination
                      .currentPageReportTemplate ||
                    '{first} - {last} de {totalRecords}'
                  "
                  [styleClass]="programacionesTableStyleClass()"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Id">Id</span>
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Tipo Cita"
                          >Tipo Cita</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Consultor"
                          >Consultor</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Fecha y Hora"
                          >Fecha y Hora</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Duración"
                          >Duración</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Cantidad"
                          >Cantidad</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Estado Cita"
                          >Estado Cita</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Estado Acta"
                          >Estado Acta</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Acciones"
                          >Acciones</span
                        >
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-prog>
                    <tr>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="tituloTabla(prog.nmconsecutivo)"
                          >{{ textoTabla(prog.nmconsecutivo) }}</span
                        >
                      </td>
                      <td class="text-center">
                        @if (esTipoCitaInforme(prog)) {
                          <i
                            class="pi pi-file text-blue-500 text-lg"
                            pTooltip="Informe"
                          ></i>
                        } @else {
                          <i
                            class="pi pi-clock text-green-500 text-lg"
                            pTooltip="Campo"
                          ></i>
                        }
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="tituloTabla(prog.nombreAsesor)"
                          >{{ textoTabla(prog.nombreAsesor) }}</span
                        >
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(formatDateTime(prog.fechaProgramacion))
                          "
                          >{{
                            textoTabla(formatDateTime(prog.fechaProgramacion))
                          }}</span
                        >
                      </td>
                      <td class="text-center">
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla((prog.horasCita || 0) + 'h')
                          "
                          >{{ textoTabla((prog.horasCita || 0) + 'h') }}</span
                        >
                      </td>
                      <td class="text-center">
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(prog.cantidadProgramada || 0)
                          "
                          >{{ textoTabla(prog.cantidadProgramada || 0) }}</span
                        >
                      </td>
                      <td>
                        <p-tag
                          [value]="prog.estado || prog.cdestado || '-'"
                          [severity]="getEstadoProgSeverity(prog.cdestado)"
                        ></p-tag>
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="tituloTabla(prog.estadoActa)"
                          >{{ textoTabla(prog.estadoActa) }}</span
                        >
                      </td>
                      <td class="text-center whitespace-nowrap">
                        @if (
                          prog.snpermiteCumplimiento === 'S' &&
                          !esCitaCancelada(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-check-circle"
                            class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                            pTooltip="Ingresar Cumplimiento"
                            (click)="ingresarCumplimiento(prog)"
                          ></button>
                        }
                        @if (
                          prog.snpermiteCancelacion === 'S' &&
                          !esCitaCancelada(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-times-circle"
                            class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                            pTooltip="Cancelar Programación"
                            (click)="cancelarProgramacion(prog)"
                          ></button>
                        }
                        @if (
                          prog.nmcumplimientoServicio && !esCitaCancelada(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-user-edit"
                            class="p-button-rounded p-button-text p-button-sm p-button-info min-w-[44px] min-h-[44px]"
                            pTooltip="Editar Contacto"
                            (click)="editarContacto(prog)"
                          ></button>
                        }
                        @if (
                          mostrarBotonAsociarActa(prog) &&
                          !esCitaCancelada(prog) &&
                          !esCitaEjecutada(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-file-edit"
                            class="p-button-rounded p-button-text p-button-sm p-button-warning min-w-[44px] min-h-[44px]"
                            pTooltip="Asociar Acta"
                            [disabled]="
                              debeDeshabilitarAsociarActa(prog) ||
                              loadingAsociarActa()
                            "
                            (click)="asociarActa(prog)"
                          ></button>
                        }
                        @if (
                          (esActaAsociada(prog) || esActaEnviadaA360(prog)) &&
                          !esCitaCancelada(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-refresh"
                            class="p-button-rounded p-button-text p-button-sm p-button-help min-w-[44px] min-h-[44px]"
                            pTooltip="Refrescar Estado"
                            [loading]="cargandoProgramaciones()"
                            (click)="refrescarProgramaciones()"
                          ></button>
                        }
                        @if (
                          tieneActaDescargable(prog) &&
                          !esCitaCancelada(prog) &&
                          !esActaEnviadaA360(prog)
                        ) {
                          <button
                            pButton
                            icon="pi pi-download"
                            class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                            pTooltip="Descargar Acta"
                            [disabled]="!!descargandoActa()"
                            (click)="descargarActa(prog)"
                          ></button>
                        }
                        <button
                          pButton
                          icon="pi pi-eye"
                          class="p-button-rounded p-button-text p-button-sm p-button-secondary min-w-[44px] min-h-[44px]"
                          pTooltip="Ver Observaciones"
                          [disabled]="!prog.dsobservaciones"
                          (click)="verObservaciones(prog)"
                        ></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            }
          </p-fieldset>

          <!-- === Tabla de Listado de Tareas para la Empresa === -->
          <p-fieldset
            legend="Listado de Tareas para la Empresa"
            [toggleable]="true"
            [collapsed]="true"
          >
            @if (cargandoTareas()) {
              <p-skeleton width="100%" height="100px"></p-skeleton>
            } @else if (tareasBeneficiario().length === 0) {
              <div class="text-center py-4 text-gray-500">
                <i class="pi pi-check-circle text-2xl mb-2"></i>
                <p>No hay tareas pendientes para esta empresa</p>
              </div>
            } @else {
              <!-- Botón Ingresar Gestión -->
              <div
                class="flex flex-col gap-2 sm:flex-row sm:justify-end sm:items-center mb-3"
              >
                <p-button
                  label="Ingresar Gestión"
                  icon="pi pi-phone"
                  severity="success"
                  size="small"
                  styleClass="w-full sm:w-auto min-h-[44px]"
                  [disabled]="tareasSeleccionadas().length === 0"
                  [badge]="
                    tareasSeleccionadas().length > 0
                      ? tareasSeleccionadas().length.toString()
                      : undefined
                  "
                  badgeClass="p-badge-warning"
                  pTooltip="Ingresar gestión de llamadas para las tareas seleccionadas"
                  (click)="abrirIngresarGestion()"
                >
                </p-button>
              </div>

              <div class="md:hidden space-y-3 mb-3">
                @for (
                  tarea of tareasBeneficiarioMobilePage();
                  track tarea.nmconsecutivo ?? $index
                ) {
                  <article
                    class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
                    [class.bg-blue-50]="tarea.esOrdenActual"
                  >
                    <div class="flex items-start justify-between gap-2 mb-2">
                      <h3
                        class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                      >
                        Orden
                        {{
                          textoTabla(tarea.codigoOrden || tarea.nmconsecutivo)
                        }}
                      </h3>
                      @if (tarea.esOrdenActual) {
                        <p-tag severity="info" value="Actual"></p-tag>
                      }
                    </div>

                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3"
                    >
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Producto:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(tarea.producto)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Tarea:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(
                            tarea.descripcionTarea || tarea.codigoTarea
                          )
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Clasificación:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(tarea.clasificacion)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Prioridad:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          tarea.prioridad === 'S' ? 'ALTA' : 'Normal'
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Saldo por Programar:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(tarea.saldoPorProgramar || 0)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Saldo Ejecutar:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(tarea.saldoPorEjecutar || 0)
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Fecha Entrega Inicial:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(formatDate(tarea.fechaEntregaInicial))
                        }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400"
                          >Fecha Entrega Final:</span
                        >
                        <span class="text-gray-800 dark:text-gray-200">{{
                          textoTabla(formatDate(tarea.fechaEntregaFinal))
                        }}</span>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <p-button
                        [label]="
                          isTareaSelected(tarea)
                            ? 'Quitar selección'
                            : 'Seleccionar'
                        "
                        [icon]="
                          isTareaSelected(tarea)
                            ? 'pi pi-check-circle'
                            : 'pi pi-circle'
                        "
                        [outlined]="true"
                        severity="secondary"
                        size="small"
                        styleClass="w-full min-h-[44px]"
                        (onClick)="
                          onTareaSelected(
                            { checked: !isTareaSelected(tarea) },
                            tarea
                          )
                        "
                      />

                      @if (!tarea.esOrdenActual) {
                        <p-button
                          label="Ver detalle"
                          icon="pi pi-search"
                          severity="primary"
                          size="small"
                          styleClass="w-full min-h-[44px]"
                          (onClick)="detallarTarea(tarea)"
                        />
                      }
                    </div>
                  </article>
                }

                <div class="flex items-center justify-between gap-2 pt-1">
                  <button
                    pButton
                    type="button"
                    label="Anterior"
                    icon="pi pi-angle-left"
                    class="p-button-text p-button-sm"
                    [disabled]="!canGoPrevTareasMobile()"
                    (click)="prevTareasMobilePage()"
                  ></button>

                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ tareasMobileFirstRecord() }} -
                    {{ tareasMobileLastRecord() }} de
                    {{ tareasBeneficiario().length }}
                  </span>

                  <button
                    pButton
                    type="button"
                    label="Siguiente"
                    icon="pi pi-angle-right"
                    iconPos="right"
                    class="p-button-text p-button-sm"
                    [disabled]="!canGoNextTareasMobile()"
                    (click)="nextTareasMobilePage()"
                  ></button>
                </div>
              </div>

              <div class="hidden md:block">
                <div
                  role="region"
                  [attr.aria-label]="tareasTableConfig().a11y.tableAriaLabel"
                  class="sr-only"
                ></div>
                <p-table
                  [resizableColumns]="true"
                  columnResizeMode="expand"
                  [value]="tareasBeneficiario()"
                  [scrollable]="true"
                  [responsiveLayout]="'scroll'"
                  [tableStyle]="{
                    'min-width':
                      tareasTableConfig().responsive.minWidth || '96rem'
                  }"
                  [paginator]="tareasTableConfig().pagination.enabled"
                  [rows]="tareasTableConfig().pagination.pageSize"
                  [rowsPerPageOptions]="
                    tareasTableConfig().pagination.pageSizeOptions
                  "
                  [paginatorDropdownAppendTo]="'body'"
                  [showCurrentPageReport]="true"
                  [currentPageReportTemplate]="
                    tareasTableConfig().pagination.currentPageReportTemplate ||
                    '{first} - {last} de {totalRecords}'
                  "
                  [styleClass]="tareasTableStyleClass()"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th
                        pResizableColumn
                        class="text-center"
                        style="width: 3rem"
                      >
                        <p-checkbox
                          [binary]="true"
                          [ngModel]="isAllTareasSelected()"
                          (onChange)="onSelectAllTareas($event)"
                          pTooltip="Seleccionar todas"
                        >
                        </p-checkbox>
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Orden"
                          >Orden</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Detallar"
                          >Detallar</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Prioridad"
                          >Prioridad</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Producto"
                          >Producto</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Tarea"
                          >Tarea</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span class="grid-header-text" title="Clasificación"
                          >Clasificación</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Saldo por Programar"
                          >Saldo por Programar</span
                        >
                      </th>
                      <th pResizableColumn class="text-center">
                        <span class="grid-header-text" title="Saldo Ejecutar"
                          >Saldo Ejecutar</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span
                          class="grid-header-text"
                          title="Fecha Entrega Inicial"
                          >Fecha Entrega Inicial</span
                        >
                      </th>
                      <th pResizableColumn>
                        <span
                          class="grid-header-text"
                          title="Fecha Entrega Final"
                          >Fecha Entrega Final</span
                        >
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-tarea>
                    <tr [class.bg-blue-50]="tarea.esOrdenActual">
                      <td class="text-center">
                        <p-checkbox
                          [binary]="true"
                          [ngModel]="isTareaSelected(tarea)"
                          (onChange)="onTareaSelected($event, tarea)"
                        >
                        </p-checkbox>
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(
                              tarea.codigoOrden || tarea.nmconsecutivo
                            )
                          "
                          >{{
                            textoTabla(tarea.codigoOrden || tarea.nmconsecutivo)
                          }}</span
                        >
                        @if (tarea.esOrdenActual) {
                          <p-tag
                            severity="info"
                            value="Actual"
                            styleClass="ml-2"
                          ></p-tag>
                        }
                      </td>
                      <td class="text-center">
                        @if (!tarea.esOrdenActual) {
                          <button
                            pButton
                            icon="pi pi-search"
                            class="p-button-rounded p-button-text p-button-sm p-button-primary min-w-[44px] min-h-[44px]"
                            pTooltip="Ver detalle de esta orden"
                            (click)="detallarTarea(tarea)"
                          ></button>
                        }
                      </td>
                      <td class="text-center">
                        @if (tarea.prioridad === 'S') {
                          <p-tag severity="danger" value="ALTA"></p-tag>
                        } @else {
                          <span class="text-gray-500">Normal</span>
                        }
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="tituloTabla(tarea.producto)"
                          >{{ textoTabla(tarea.producto) }}</span
                        >
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(
                              tarea.descripcionTarea || tarea.codigoTarea
                            )
                          "
                          >{{
                            textoTabla(
                              tarea.descripcionTarea || tarea.codigoTarea
                            )
                          }}</span
                        >
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="tituloTabla(tarea.clasificacion)"
                          >{{ textoTabla(tarea.clasificacion) }}</span
                        >
                      </td>
                      <td class="text-center">
                        <span
                          [class.text-red-600]="
                            (tarea.saldoPorProgramar || 0) > 0
                          "
                          [class.font-bold]="(tarea.saldoPorProgramar || 0) > 0"
                        >
                          {{ tarea.saldoPorProgramar || 0 }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          [class.text-orange-600]="
                            (tarea.saldoPorEjecutar || 0) > 0
                          "
                        >
                          {{ tarea.saldoPorEjecutar || 0 }}
                        </span>
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(formatDate(tarea.fechaEntregaInicial))
                          "
                          >{{
                            textoTabla(formatDate(tarea.fechaEntregaInicial))
                          }}</span
                        >
                      </td>
                      <td>
                        <span
                          class="grid-cell-text"
                          [attr.title]="
                            tituloTabla(formatDate(tarea.fechaEntregaFinal))
                          "
                          >{{
                            textoTabla(formatDate(tarea.fechaEntregaFinal))
                          }}</span
                        >
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            }
          </p-fieldset>
        </div>
      } @else {
        <!-- Error o sin datos -->
        <div class="text-center py-8">
          <i class="pi pi-exclamation-circle text-4xl text-orange-500 mb-4"></i>
          <p class="text-gray-600">No se pudo cargar el detalle de la orden</p>
        </div>
      }

      <!-- Footer con acciones -->
      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="cerrar()"
          >
          </p-button>
          <p-button
            label="Historial Gestiones"
            icon="pi pi-phone"
            severity="primary"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="verHistorialGestiones()"
            [disabled]="!detalle()"
          >
          </p-button>
          <p-button
            label="Contactos"
            icon="pi pi-users"
            severity="info"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="verContactos()"
            [disabled]="!detalle()"
          >
          </p-button>
          <p-button
            label="Programar Cita"
            icon="pi pi-calendar-plus"
            severity="success"
            styleClass="min-h-[44px]"
            (onClick)="abrirProgramarCita()"
            [disabled]="!permiteProgramar()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Modal de Observaciones -->
    <p-dialog
      header="Observaciones de la Programación"
      [visible]="mostrarObservaciones()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [style]="{ width: '92vw', maxWidth: '32rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      (visibleChange)="mostrarObservaciones.set($event)"
    >
      <div class="p-4">
        @if (programacionObservaciones()?.dsobservaciones) {
          <p class="whitespace-pre-wrap text-gray-700">
            {{ programacionObservaciones()?.dsobservaciones }}
          </p>
        } @else {
          <p class="text-gray-500 italic">
            No hay observaciones registradas para esta programación.
          </p>
        }
      </div>
      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="mostrarObservaciones.set(false)"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Modal de Editar Contacto Cumplimiento -->
    <p-dialog
      header="Editar contacto cumplimiento"
      [visible]="mostrarEditarContacto()"
      [modal]="true"
      [closable]="!guardandoContacto()"
      [draggable]="false"
      [style]="{ width: '92vw', maxWidth: '30rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      (visibleChange)="cerrarModalContacto($event)"
    >
      <div class="flex flex-col gap-4 p-2">
        <div class="flex flex-col gap-1">
          <label for="contactoNombre" class="font-medium text-sm"
            >Nombre del Contacto</label
          >
          <input
            pInputText
            id="contactoNombre"
            [value]="contactoNombre()"
            (input)="contactoNombre.set($any($event.target).value)"
            (change)="contactoNombre.set(sanitizeText(contactoNombre()))"
            maxlength="50"
            [disabled]="guardandoContacto()"
            class="w-full"
            placeholder="Ingrese el nombre del contacto"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label for="contactoEmail" class="font-medium text-sm"
            >Email del Contacto</label
          >
          <input
            pInputText
            id="contactoEmail"
            type="email"
            [value]="contactoEmail()"
            (keypress)="emailKeypress($event)"
            (input)="contactoEmail.set($any($event.target).value)"
            (change)="contactoEmail.set(sanitizeEmail(contactoEmail()))"
            maxlength="100"
            [disabled]="guardandoContacto()"
            class="w-full"
            placeholder="ejemplo@correo.com"
          />
        </div>
      </div>
      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            [disabled]="guardandoContacto()"
            styleClass="min-h-[44px]"
            (onClick)="cerrarModalContacto(false)"
          >
          </p-button>
          <p-button
            label="Guardar"
            icon="pi pi-check"
            [loading]="guardandoContacto()"
            styleClass="min-h-[44px]"
            (onClick)="guardarContacto()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Modal Cancelar Programación -->
    @if (mostrarCancelarProgramacion() && programacionACancelar()) {
      <app-cancelar-cita-dialog
        [citaId]="programacionACancelar()!.nmconsecutivo"
        [cita]="adaptarProgramacionParaCancelacion(programacionACancelar()!)"
        [datosProgramador]="datosProgramadorSesion()"
        cancelarFuturas="N"
        (onClose)="cerrarModalCancelacion()"
        (onConfirm)="onCancelacionConfirmada($event)"
      >
      </app-cancelar-cita-dialog>
    }

    <!-- Modal Historial de Gestiones -->
    <app-historial-gestiones-dialog
      [visible]="mostrarHistorialGestiones()"
      [nmconsecutivoDetalle]="nmconsecutivoDetalleHistorial()"
      [nombreBeneficiario]="detalle()?.nombreBeneficiario ?? null"
      [tarea]="detalle()?.tareaCliente ?? null"
      (onClose)="cerrarHistorialGestiones()"
    >
    </app-historial-gestiones-dialog>

    <app-gestionar-contactos-informe-dialog #contactosInformeDialog />

    <!-- Modal de Contactos del Centro de Trabajo / Beneficiario -->
    @if (mostrarContactos()) {
      <app-contactos-trabajo-dialog
        [contactos]="contactosLista()"
        [paginacionServidor]="paginacionContactos()"
        [loading]="loadingContactos()"
        [errorGuardado]="errorGuardadoContactos()"
        (onPaginar)="onPaginarContactos($event)"
        [cdpersona]="detalle()?.cdpersBeneficiario ?? null"
        [ocultarSeleccionar]="true"
        (onSave)="onContactoCreado($event)"
        (onUpdate)="onContactoModificado($event)"
        (onDelete)="onContactoEliminado($event)"
        (onClose)="cerrarContactos()"
      />
    }

    <!-- Modal de Ingresar Gestión de Llamadas -->
    <app-ingresar-gestion-dialog
      [visible]="mostrarIngresarGestion()"
      [tareasSeleccionadas]="tareasSeleccionadas()"
      (onClose)="cerrarIngresarGestion()"
      (onGestionGuardada)="onGestionGuardada($event)"
    >
    </app-ingresar-gestion-dialog>

    <!-- Modal de Seleccionar Asesor para Programar Cita -->
    <app-seleccionar-asesor-dialog
      [visible]="mostrarSeleccionarAsesor()"
      [nmconsecutivoTarea]="detalle()?.nmconsecutivo"
      (onClose)="cerrarSeleccionarAsesor()"
      (onSeleccionar)="onAsesorSeleccionado($event)"
    >
    </app-seleccionar-asesor-dialog>

    <!-- Modal de Agenda Asesor para Programar Cita (usuarios Asesor o después de seleccionar asesor) -->
    <app-agenda-asesor-dialog
      [visible]="mostrarAgendaAsesor()"
      [ordenServicioDetalle]="detalle()"
      [dniAsesor]="dniAsesorAgenda()"
      [nmconsecutivoAsesor]="nmconsecutivoAsesorAgenda()"
      [nombreAsesor]="nombreAsesorAgenda()"
      (closed)="cerrarAgendaAsesor()"
      (citaCreada)="onCitaCreadaDesdeAgenda($event)"
    >
    </app-agenda-asesor-dialog>

    <!-- Modal Registrar / Asociar Acta -->
    <app-formulario-cumplimiento
      [visible]="dialogCumplimientoVisible()"
      [programacion]="programacionParaCumplimiento()"
      [saldoActual]="saldoCumplimiento()"
      [loading]="loadingCumplimiento()"
      [esTipoCampo]="esTipoCampo()"
      [esTipoInforme]="esTipoInforme()"
      [requiereJustificacion]="requiereJustificacion()"
      [justificaciones]="justificaciones()"
      [municipioOrigenInicial]="municipioOrigenInicialCumplimiento()"
      [municipioEntregaInicial]="municipioEntregaInicialCumplimiento()"
      [soloActa]="soloActaModo()"
      [contratosExentos]="contratosExentosCumplString()"
      [actaGenerando]="loadingAsociarActa()"
      [mostrarBotonDescargarActa]="mostrarBotonDescargarActa()"
      [descargandoActa]="descargandoActaAsociada()"
      (visibleChange)="onDialogCumplimientoVisibleChange($event)"
      (guardar)="onGuardarCumplimiento($event)"
      (reintentar)="onReintentarAsociarActa($event)"
      (descargarActa)="onDescargarActaAsociada()"
      (cancelar)="cerrarDialogCumplimiento()"
      (fechaCumplimientoChange)="onFechaCumplimientoChange($event)"
      (reportarAlertaTemprana)="reportarAlertaTemprana()"
      (verHistorial)="onVerHistorialCumplimientos()"
    >
    </app-formulario-cumplimiento>

    <!-- Historial de Cumplimientos Dialog -->
    <app-historial-cumplimientos-dialog
      [visible]="historialCumplimientosVisible()"
      [cumplimientos]="historialCumplimientos()"
      (cerrar)="historialCumplimientosVisible.set(false)"
      (recargarHistorial)="onVerHistorialCumplimientos()"
    >
    </app-historial-cumplimientos-dialog>
  `
})
export class ProgramarServicioDialogComponent implements OnInit, OnDestroy {
  // === XSS Sanitization ===
  sanitizeText = sanitizeText;
  sanitizeEmail = sanitizeEmail;
  emailKeypress = emailKeypress;

  // === Inputs/Outputs ===
  visible = input.required<boolean>();
  ordenSeleccionada = input.required<OrdenServicio | null>();
  nombreAseguradora = input<string>('');

  onClose = output<void>();
  onProgramarCita = output<OrdenServicioDetalleProgramar>();
  onCambiarOrden = output<TareaBeneficiario>();

  // === Servicios ===
  private readonly ordenServicioRepo = inject(OrdenServicioRepository);
  private readonly documentoP8Service = inject(DocumentoP8Service);
  private readonly agendaRepository = inject(AgendaRepository);
  private readonly bloqueoProgramacionRepo = inject(
    BloqueoProgramacionRepository
  );
  private readonly messageService = inject(MessageService);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly contactoPersonaRepo = inject(ContactoPersonaRepository);
  private readonly cumplimientoRepo = inject(CumplimientoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly router = inject(Router);

  /** Código del tipo de empleado Asesor desde TPSC_PARAMETROS_GENERALES (default: 'A') */
  private cdtipoEmpleadoAsesor = signal<string>('A');

  /** URL del servicio G360 para asociar actas (desde TPSC_PARAMETROS_GENERALES.URL_SRV_G360) */
  // ELIMINADO: la integración G360 vía ventana externa se reemplazó por el flujo HU1132419
  // (envío de datos del acta desde el form y generación automática en backend).

  /** Contratos excluidos de asociación automática de actas (desde /ordenes-servicio/configuracion). */
  private contratosCumplExcluidos = signal<string[]>([]);

  /** Version CSV de `contratosCumplExcluidos` para pasar al formulario-cumplimiento.
   *  Legacy: CreateOrUpdateCumplimientoViewImpl.isContratoExentoActa() compara contra
   *  el parámetro CONTRATOS_CUMPL (string separado por comas). */
  protected contratosExentosCumplString = computed(() =>
    this.contratosCumplExcluidos().join(',')
  );

  // === Estado ===
  cargando = signal(false);
  cargandoProgramaciones = signal(false);
  cargandoTareas = signal(false);
  descargandoActa = signal<number | null>(null);

  // Modal de observaciones
  mostrarObservaciones = signal(false);
  programacionObservaciones = signal<ProgramacionServicio | null>(null);

  // Modal de editar contacto
  mostrarEditarContacto = signal(false);
  programacionEditarContacto = signal<ProgramacionServicio | null>(null);
  contactoNombre = signal('');
  contactoEmail = signal('');
  guardandoContacto = signal(false);

  // Modal de cancelar programación
  mostrarCancelarProgramacion = signal(false);
  programacionACancelar = signal<ProgramacionServicio | null>(null);
  cancelandoProgramacion = signal(false);

  // Modal de historial de gestiones
  mostrarHistorialGestiones = signal(false);

  // Modal de contactos
  mostrarContactos = signal(false);
  // Paginación REAL en servidor: en memoria solo vive la página actual (legacy: start/length, pageSize 10)
  private readonly contactosPager = new ContactosCursorPager((cursor, size, direction) => {
    const det = this.detalle();
    if (det?.nmconsecCentroTrabajo) {
      return this.contactoPersonaRepo.getByCentroTrabajoPaged(
        det.nmconsecCentroTrabajo,
        cursor,
        size,
        direction
      );
    }
    if (det?.cdpersBeneficiario) {
      return this.contactoPersonaRepo.getByPersonaPaged(
        det.cdpersBeneficiario,
        true,
        cursor,
        size,
        direction
      );
    }
    return Promise.resolve({
      content: [],
      size,
      nextCursor: null,
      previousCursor: null,
      hasNext: false,
      hasPrevious: false,
      totalElements: 0
    });
  });
  contactosLista = computed<ContactoCentroTrabajo[]>(() =>
    this.contactosPager.items().map((c) => ({
      nmconsecutivo: c.nmconsecutivo,
      cdpersona: c.cdpersona,
      dsnombre: c.dsnombre || '',
      dscargo: c.dscargo || c.dscargo_desc,
      dstelefonoFijo: c.dstelefono,
      dstelefonoCelular: c.dscelular,
      dsemail: c.dsemail
    }))
  );
  readonly paginacionContactos = this.contactosPager.paginacion;
  loadingContactos = signal(false);
  /** Legacy: tras registrar un cumplimiento Final de tipo INFORME se gestionan los contactos del informe. */
  @ViewChild('contactosInformeDialog') contactosInformeDialog?: GestionarContactosInformeDialogComponent;
  /** Se incrementa cuando falla guardar un contacto: el diálogo reabre el formulario con lo digitado. */
  errorGuardadoContactos = signal(0);

  detalle = signal<OrdenServicioDetalleProgramar | null>(null);
  programaciones = signal<ProgramacionServicio[]>([]);
  tareasBeneficiario = signal<TareaBeneficiario[]>([]);

  readonly programacionesTableConfig = computed(() =>
    programarServicioProgramacionesPreset.createConfig({
      data: this.programaciones() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-programar-servicio',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 5,
        pageSizeOptions: [5, 10, 20],
        currentPageReportTemplate: '{first} - {last} de {totalRecords}'
      },
      state: {
        loading: this.cargandoProgramaciones(),
        emptyMessage: 'No hay programaciones registradas para esta orden'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '92rem',
        scrollHeight: 'auto',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de programaciones de la orden seleccionada',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly programacionesTableStyleClass = computed(() => {
    const config = this.programacionesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly tareasTableConfig = computed(() =>
    programarServicioTareasPreset.createConfig({
      data: this.tareasBeneficiario() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-programar-servicio',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 5,
        pageSizeOptions: [5, 10, 20],
        currentPageReportTemplate: '{first} - {last} de {totalRecords}'
      },
      state: {
        loading: this.cargandoTareas(),
        emptyMessage: 'No hay tareas pendientes para esta empresa'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '96rem',
        scrollHeight: 'auto',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de listado de tareas para la empresa',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tareasTableStyleClass = computed(() => {
    const config = this.tareasTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // === Paginación visual en mobile (tarjetas) ===
  private readonly mobileRows = 5;
  programacionesMobilePageIndex = signal(0);
  tareasMobilePageIndex = signal(0);

  programacionesMobilePage = computed(() => {
    const start = this.programacionesMobilePageIndex() * this.mobileRows;
    return this.programaciones().slice(start, start + this.mobileRows);
  });

  tareasBeneficiarioMobilePage = computed(() => {
    const start = this.tareasMobilePageIndex() * this.mobileRows;
    return this.tareasBeneficiario().slice(start, start + this.mobileRows);
  });

  // === Selección de tareas para Ingresar Gestión ===
  tareasSeleccionadas = signal<TareaBeneficiario[]>([]);
  mostrarIngresarGestion = signal(false);

  // === Seleccionar Asesor para Programar Cita ===
  mostrarSeleccionarAsesor = signal(false);
  asesorSeleccionado = signal<AsesorSugerido | null>(null);
  /** DNI del asesor seleccionado (enviado en asesores sugeridos) */
  private dniAsesorSeleccionado = signal<string | null>(null);

  // === Agenda Asesor para Programar Cita (usuarios tipo Asesor) ===
  mostrarAgendaAsesor = signal(false);

  // === Modal Registrar Cumplimiento ===
  dialogCumplimientoVisible = signal(false);
  programacionParaCumplimiento = signal<ProgramacionPendiente | null>(null);

  // === Historial de Cumplimientos ===
  historialCumplimientosVisible = signal(false);
  historialCumplimientos = signal<HistorialCumplimiento[]>([]);
  loadingHistorial = signal(false);
  saldoCumplimiento = signal(0);
  loadingCumplimiento = signal(false);
  esTipoCampo = signal(false);
  esTipoInforme = signal(false);
  requiereJustificacion = signal(false);
  justificaciones = signal<{ label: string; value: string }[]>([]);
  // Municipios precargados para el formulario de cumplimiento
  municipioOrigenInicialCumplimiento = signal<MunicipioPrecargado | null>(null);
  municipioEntregaInicialCumplimiento = signal<MunicipioPrecargado | null>(
    null
  );

  // === Modo "Asociar Acta" (reuso del form de cumplimiento solo para acta) ===
  /** Cuando true, el form de cumplimiento se abre en modo "solo acta" (legacy: ProgramarServiciosViewImpl.asociarActas). */
  soloActaModo = signal(false);
  /** Programación cuyo cumplimiento se está re-asociando (modo soloActa). */
  cumplimientoExistenteAsociar = signal<ProgramacionServicio | null>(null);
  /** Loading mientras el backend reintenta generar/asociar el acta. */
  loadingAsociarActa = signal(false);
  /** Muestra el botón de descarga del acta tras una asociación exitosa. */
  mostrarBotonDescargarActa = signal(false);
  /** Loading del botón de descarga del acta asociada en el modal. */
  descargandoActaAsociada = signal(false);
  /** Consecutivo del cumplimiento cuyo acta se puede descargar desde el modal. */
  private nmconsecutivoCumplimientoDescargable: number | null = null;

  // === Computed ===
  permiteProgramar = computed(() => {
    const d = this.detalle();
    if (!d) return false;
    // Permitir si hay saldo por programar y no está bloqueada
    return (d.saldoPorProgramar || 0) > 0 && d.snBloqueadaUsuario !== 'S';
  });

  /**
   * Consecutivo usado por el historial de gestiones.
   * Debe priorizar la orden actualmente detallada en el diálogo para evitar
   * mostrar historial de una orden previa al usar la acción "Detallar".
   */
  nmconsecutivoDetalleHistorial = computed(() => {
    const detalleActual = this.detalle()?.nmconsecutivo;
    if (detalleActual != null) {
      return detalleActual;
    }
    return this.ordenSeleccionada()?.nmconsecutivo ?? null;
  });

  /**
   * DNI del asesor para el diálogo de agenda.
   * - Si hay un asesor seleccionado: usa el DNI consultado bajo demanda.
   * - Si no: usa el DNI del usuario logueado (cuando es asesor).
   */
  dniAsesorAgenda = computed(() => {
    // Si hay asesor seleccionado, usar su DNI consultado
    const dni = this.dniAsesorSeleccionado();
    if (dni) {
      return dni;
    }
    // Si es asesor logueado, usar su DNI
    const asesor = this.asesorSeleccionado();
    const usuario = this.usuarioState.usuario();
    if (!asesor && usuario?.dni) {
      return usuario.dni;
    }
    return '';
  });

  /**
   * Consecutivo del asesor para el diálogo de agenda.
   * Usado como fallback si no hay DNI disponible.
   */
  nmconsecutivoAsesorAgenda = computed(() => {
    const asesor = this.asesorSeleccionado();
    if (asesor?.nmconsecutivoAsesor) return asesor.nmconsecutivoAsesor;
    const usuario = this.usuarioState.usuario();
    return usuario?.nmconsecutivo || null;
  });

  /**
   * Nombre del asesor para el encabezado del diálogo de agenda.
   */
  nombreAsesorAgenda = computed(() => {
    const asesor = this.asesorSeleccionado();
    if (asesor?.nombre) return asesor.nombre;
    const usuario = this.usuarioState.usuario();
    return usuario?.nombreCompleto || '';
  });

  // === Effects ===
  private loadEffect = effect(() => {
    const orden = this.ordenSeleccionada();
    const isVisible = this.visible();

    if (isVisible && orden) {
      this.cargarDatos(orden);
    } else if (!isVisible) {
      // Limpiar al cerrar
      this.detalle.set(null);
      this.programaciones.set([]);
      this.tareasBeneficiario.set([]);
      this.tareasSeleccionadas.set([]);
      this.programacionesMobilePageIndex.set(0);
      this.tareasMobilePageIndex.set(0);
    }
  });

  ngOnInit(): void {
    // Carga contratos y tipo asesor desde configuración dedicada de órdenes.
    void this.cargarContratosCumplExcluidos();
  }

  ngOnDestroy(): void {
    // Limpieza
  }

  // === Carga de datos ===

  private async cargarDatos(orden: OrdenServicio): Promise<void> {
    this.cargando.set(true);

    try {
      // Cargar detalle
      console.log(
        '[ProgramarServicio] Consultando detalle para orden:',
        orden.nmconsecutivo
      );
      const detalle = await this.ordenServicioRepo.obtenerDetalle(
        orden.nmconsecutivo
      );
      if (!environment.production) {
        console.log(
          '[ProgramarServicio] Detalle recibido para orden:',
          orden.nmconsecutivo
        );
      }
      console.log(
        '[ProgramarServicio] orden.nmconsecutivoBeneficiario:',
        orden.nmconsecutivoBeneficiario
      );
      console.log(
        '[ProgramarServicio] detalle.nmconsecutivoBeneficiario:',
        detalle.nmconsecutivoBeneficiario
      );
      this.detalle.set(detalle);

      // Cargar programaciones y contratos excluidos en paralelo
      this.cargarProgramaciones(orden.nmconsecutivo);
      this.cargarContratosCumplExcluidos();

      // Cargar tareas del beneficiario usando nmconsecutivoBeneficiario de la orden (viene del listado)
      // El SP de detalle no lo devuelve, pero el SP de consulta s?
      const nmconsecutivoBeneficiario =
        orden.nmconsecutivoBeneficiario ?? detalle.nmconsecutivoBeneficiario;
      console.log(
        '[ProgramarServicio] nmconsecutivoBeneficiario - orden:',
        orden.nmconsecutivoBeneficiario,
        'detalle:',
        detalle.nmconsecutivoBeneficiario,
        'usado:',
        nmconsecutivoBeneficiario
      );
      if (nmconsecutivoBeneficiario != null && nmconsecutivoBeneficiario > 0) {
        this.cargarTareasBeneficiario(
          nmconsecutivoBeneficiario,
          orden.nmconsecutivo
        );
      } else {
        console.warn(
          '[ProgramarServicio] No se puede cargar tareas: nmconsecutivoBeneficiario es',
          nmconsecutivoBeneficiario
        );
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el detalle de la orden'
      });
    } finally {
      this.cargando.set(false);
    }
  }

  private async cargarProgramaciones(nmconsecutivo: number): Promise<void> {
    console.log(
      '[ProgramarServicio] Cargando programaciones para nmconsecutivo:',
      nmconsecutivo
    );
    this.cargandoProgramaciones.set(true);

    try {
      const progs =
        await this.ordenServicioRepo.consultarProgramaciones(nmconsecutivo);
      console.log('[ProgramarServicio] Programaciones recibidas:', progs);
      this.programaciones.set(progs);
      this.programacionesMobilePageIndex.set(0);
    } catch (error) {
      console.error(
        '[ProgramarServicio] Error cargando programaciones:',
        error
      );
    } finally {
      this.cargandoProgramaciones.set(false);
    }
  }

  private async cargarTareasBeneficiario(
    nmconsecutivoBeneficiario: number,
    nmconsecutivoOrden: number
  ): Promise<void> {
    console.log(
      '[ProgramarServicio] Cargando tareas del beneficiario:',
      nmconsecutivoBeneficiario,
      'orden:',
      nmconsecutivoOrden
    );
    this.cargandoTareas.set(true);

    try {
      // Cargar todos los registros - paginación del lado del cliente.
      // El adapter valida size <= PAGE_SIZE_MAX (100), así que se recorren
      // las páginas necesarias del backend y se agregan en el cliente.
      const MAX_PAGINAS = 20; // límite de seguridad: hasta 2000 registros
      const todas: TareaBeneficiario[] = [];
      let totalElements = 0;
      let page = 0;
      let esUltimaPagina = false;

      while (!esUltimaPagina && page < MAX_PAGINAS) {
        const pageResult =
          await this.ordenServicioRepo.consultarTareasBeneficiario(
            nmconsecutivoBeneficiario,
            nmconsecutivoOrden,
            page,
            PAGE_SIZE_MAX
          );
        todas.push(...pageResult.content);
        totalElements = pageResult.totalElements;
        esUltimaPagina = pageResult.last || pageResult.content.length === 0;
        page++;
      }

      console.log(
        '[ProgramarServicio] Tareas recibidas:',
        todas.length,
        'total:',
        totalElements
      );
      this.tareasBeneficiario.set(todas);
      this.tareasMobilePageIndex.set(0);
    } catch (error) {
      console.error('Error cargando tareas beneficiario:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el listado de tareas para la empresa'
      });
    } finally {
      this.cargandoTareas.set(false);
    }
  }

  // === Acciones ===

  canGoPrevProgramacionesMobile(): boolean {
    return this.programacionesMobilePageIndex() > 0;
  }

  canGoNextProgramacionesMobile(): boolean {
    const totalPages = Math.ceil(
      this.programaciones().length / this.mobileRows
    );
    return this.programacionesMobilePageIndex() < totalPages - 1;
  }

  prevProgramacionesMobilePage(): void {
    if (this.canGoPrevProgramacionesMobile()) {
      this.programacionesMobilePageIndex.update((v) => v - 1);
    }
  }

  nextProgramacionesMobilePage(): void {
    if (this.canGoNextProgramacionesMobile()) {
      this.programacionesMobilePageIndex.update((v) => v + 1);
    }
  }

  programacionesMobileFirstRecord(): number {
    const total = this.programaciones().length;
    if (total === 0) return 0;
    return this.programacionesMobilePageIndex() * this.mobileRows + 1;
  }

  programacionesMobileLastRecord(): number {
    const total = this.programaciones().length;
    if (total === 0) return 0;
    return Math.min(
      (this.programacionesMobilePageIndex() + 1) * this.mobileRows,
      total
    );
  }

  canGoPrevTareasMobile(): boolean {
    return this.tareasMobilePageIndex() > 0;
  }

  canGoNextTareasMobile(): boolean {
    const totalPages = Math.ceil(
      this.tareasBeneficiario().length / this.mobileRows
    );
    return this.tareasMobilePageIndex() < totalPages - 1;
  }

  prevTareasMobilePage(): void {
    if (this.canGoPrevTareasMobile()) {
      this.tareasMobilePageIndex.update((v) => v - 1);
    }
  }

  nextTareasMobilePage(): void {
    if (this.canGoNextTareasMobile()) {
      this.tareasMobilePageIndex.update((v) => v + 1);
    }
  }

  tareasMobileFirstRecord(): number {
    const total = this.tareasBeneficiario().length;
    if (total === 0) return 0;
    return this.tareasMobilePageIndex() * this.mobileRows + 1;
  }

  tareasMobileLastRecord(): number {
    const total = this.tareasBeneficiario().length;
    if (total === 0) return 0;
    return Math.min(
      (this.tareasMobilePageIndex() + 1) * this.mobileRows,
      total
    );
  }

  /**
   * Abre el flujo de Programar Cita.
   *
   * Si el usuario NO es tipo "Asesor", primero debe seleccionar un asesor/consultor
   * del listado de sugeridos (SP_GET_POSIBLES_ASESORES).
   *
   * Legacy: ProgramarServiciosActivity.programar() -> ScheduleAdvisorPlace
   */
  abrirProgramarCita(): void {
    const d = this.detalle();
    if (!d) return;

    // Obtener tipo de empleado del usuario logueado
    const usuario = this.usuarioState.usuario();
    const cdtipoEmpleado = usuario?.cdtipoEmpleado;

    console.log(
      '[ProgramarServicio] Usuario tipo empleado:',
      cdtipoEmpleado,
      usuario?.descripcionTipoEmpleado
    );

    // Si es tipo Asesor (según parámetro CDTIPO_EMP_ASES), proceder directamente
    // Si NO es Asesor, mostrar diálogo de selección de asesor
    // Legacy: if (!TipoEmpleadoEnum.ASESOR.getDescripcion().equals(empleado.getCdtipo_empleado()))
    if (cdtipoEmpleado === this.cdtipoEmpleadoAsesor()) {
      // Usuario es Asesor: proceder directamente con el flujo de programar cita
      console.log(
        '[ProgramarServicio] Usuario es Asesor, procediendo directamente'
      );
      this.procederProgramarCita(d);
    } else {
      console.log(
        '[ProgramarServicio] Usuario no es Asesor, mostrando diálogo de selección'
      );
      this.mostrarSeleccionarAsesor.set(true);
    }
  }

  /**
   * Procede con el flujo de programar cita (con o sin asesor seleccionado).
   */
  private procederProgramarCita(detalle: OrdenServicioDetalleProgramar): void {
    const usuario = this.usuarioState.usuario();
    const cdtipoEmpleado = usuario?.cdtipoEmpleado;

    // Para usuarios tipo Asesor: mostrar el calendario de agenda del asesor
    if (cdtipoEmpleado === this.cdtipoEmpleadoAsesor()) {
      console.log('[ProgramarServicio] Usuario es Asesor, mostrando agenda');
      this.mostrarAgendaAsesor.set(true);
    } else {
      // Para otros usuarios: verificar que hay un asesor seleccionado
      const asesor = this.asesorSeleccionado();
      if (asesor) {
        // Mostrar agenda del asesor seleccionado
        this.mostrarAgendaAsesor.set(true);
      } else {
        // No debería llegar aquí, pero por seguridad emitimos el evento
        this.onProgramarCita.emit(detalle);
        this.messageService.add({
          severity: 'warn',
          summary: 'Consultor requerido',
          detail: 'Debe seleccionar un consultor primero'
        });
      }
    }
  }

  // === Seleccionar Asesor ===

  /**
   * Cierra el diálogo de selección de asesor
   */
  cerrarSeleccionarAsesor(): void {
    this.mostrarSeleccionarAsesor.set(false);
  }

  /**
   * Maneja la selección de un asesor del diálogo.
   */
  async onAsesorSeleccionado(asesor: AsesorSugerido): Promise<void> {
    this.asesorSeleccionado.set(asesor);
    this.mostrarSeleccionarAsesor.set(false);

    this.dniAsesorSeleccionado.set(asesor.dni ?? null);

    // Proceder con el flujo de programar cita
    const d = this.detalle();
    if (d) {
      this.procederProgramarCita(d);
    }
  }

  // === Agenda Asesor ===

  /**
   * Cierra el diálogo de agenda del asesor.
   * Al cerrar, recarga el detalle de la orden y las programaciones para reflejar los cambios.
   */
  async cerrarAgendaAsesor(): Promise<void> {
    this.mostrarAgendaAsesor.set(false);

    // Limpiar asesor seleccionado para próximas programaciones
    this.asesorSeleccionado.set(null);
    this.dniAsesorSeleccionado.set(null);

    // Recargar el detalle de la orden para actualizar saldos y las programaciones
    const orden = this.ordenSeleccionada();
    if (orden?.nmconsecutivo) {
      try {
        const detalle = await this.ordenServicioRepo.obtenerDetalle(
          orden.nmconsecutivo
        );
        this.detalle.set(detalle);

        // Recargar las programaciones para reflejar las citas creadas/modificadas
        await this.cargarProgramaciones(orden.nmconsecutivo);
      } catch (error) {
        console.error('[ProgramarServicio] Error recargando detalle:', error);
      }
    }
  }

  /**
   * Maneja la creación exitosa de una cita desde el diálogo de agenda.
   * NO cierra el diálogo de agenda - el usuario puede seguir programando más citas.
   * Solo recarga las programaciones de la orden.
   */
  async onCitaCreadaDesdeAgenda(cita: any): Promise<void> {
    console.log(
      '[ProgramarServicio] Cita creada desde agenda:',
      cita?.nmconsecutivoProg
    );

    // NO cerrar el diálogo de agenda - el usuario puede programar más citas
    // La agenda ya se refrescó internamente al crear la cita

    // Recargar las programaciones de la orden
    const d = this.detalle();
    if (d?.nmconsecutivo) {
      await this.cargarProgramaciones(d.nmconsecutivo);
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Cita programada',
      detail: 'La cita se ha programado correctamente'
    });
  }

  // === Acciones de Programaciones ===

  editarContacto(prog: ProgramacionServicio): void {
    if (!prog.nmcumplimientoServicio) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin cumplimiento',
        detail:
          'Esta programación no tiene un cumplimiento asociado para editar el contacto'
      });
      return;
    }

    // Cargar datos actuales del contacto
    this.programacionEditarContacto.set(prog);
    this.contactoNombre.set(prog.nombreContacto || '');
    this.contactoEmail.set(prog.correoContacto || '');
    this.mostrarEditarContacto.set(true);
  }

  cerrarModalContacto(visible: boolean): void {
    if (!visible) {
      this.mostrarEditarContacto.set(false);
      this.programacionEditarContacto.set(null);
      this.contactoNombre.set('');
      this.contactoEmail.set('');
    }
  }

  async guardarContacto(): Promise<void> {
    const prog = this.programacionEditarContacto();
    if (!prog?.nmcumplimientoServicio) {
      return;
    }

    const nombreContacto = this.sanitizeText(this.contactoNombre().trim());
    const emailContacto = this.sanitizeEmail(this.contactoEmail().trim());

    // Legacy (ProgramarServiciosViewImpl): nombre y correo obligatorios; correo con REGEX_EMAILS
    if (!nombreContacto) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre de contacto es obligatorio'
      });
      return;
    }

    if (!emailContacto) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El email de contacto es obligatorio'
      });
      return;
    }

    if (!isValidEmailContactoLegacy(emailContacto)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Ingrese un email válido'
      });
      return;
    }

    this.contactoNombre.set(nombreContacto);
    this.contactoEmail.set(emailContacto);

    this.guardandoContacto.set(true);

    try {
      const actualizado = await this.ordenServicioRepo.actualizarContactoCumplimiento(
        prog.nmcumplimientoServicio,
        nombreContacto,
        emailContacto
      );
      // Legacy: si el cumplimiento no existe falla (findById); no se informa un éxito falso
      if (!actualizado) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo actualizar el contacto. Por favor intente nuevamente.'
        });
        return;
      }

      // Actualizar la programación en la lista local
      const programacionesActuales = this.programaciones();
      const index = programacionesActuales.findIndex(
        (p) => p.nmconsecutivo === prog.nmconsecutivo
      );
      if (index !== -1) {
        const actualizada = {
          ...programacionesActuales[index],
          nombreContacto,
          correoContacto: emailContacto
        };
        const nuevas = [...programacionesActuales];
        nuevas[index] = actualizada;
        this.programaciones.set(nuevas);
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Contacto actualizado',
        detail: 'La información se ha guardado con éxito'
      });

      this.cerrarModalContacto(false);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.obtenerMensajeErrorGuardado(
          error,
          'No se pudo actualizar el contacto. Por favor intente nuevamente.'
        )
      });
    } finally {
      this.guardandoContacto.set(false);
    }
  }

  /**
   * Carga los contratos excluidos de asociacion automatica de actas
   * desde el contrato dedicado de Ordenes de Servicio.
   */
  private async cargarContratosCumplExcluidos(): Promise<void> {
    try {
      const configuracion = await this.ordenServicioRepo.obtenerConfiguracionActas();
      const contratos = Array.isArray(configuracion?.contratosCumpl)
        ? configuracion.contratosCumpl
            .map((c) => String(c ?? '').trim())
            .filter((c) => c.length > 0)
        : [];
      if (contratos.length > 0) {
        this.contratosCumplExcluidos.set(contratos);
        console.log(
          '[ProgramarServicio] Contratos excluidos de G360 cargados:',
          contratos
        );
      } else {
        this.contratosCumplExcluidos.set([]);
      }

      const tipoEmpleadoAsesor = String(
        configuracion?.cdtipoEmpleadoAsesor ?? ''
      )
        .trim()
        .toUpperCase();
      if (tipoEmpleadoAsesor) {
        this.cdtipoEmpleadoAsesor.set(tipoEmpleadoAsesor);
        console.log(
          '[ProgramarServicio] Tipo empleado asesor cargado:',
          tipoEmpleadoAsesor
        );
      }
    } catch (error) {
      console.error(
        '[ProgramarServicio] Error cargando configuración de órdenes:',
        error
      );
      this.contratosCumplExcluidos.set([]);
    }
  }

  /**
   * Abre el formulario en modo "Asociar Acta" para un cumplimiento ya creado.
   *
   * Legacy: ProgramarServiciosViewImpl.onRowLinkClickEvent (columna "Asociar")
   *   -> activity.ingresarCumplimiento(object) reabre el form de cumplimiento.
   *   El usuario ingresa los datos del acta y al guardar se genera/asocia en P8.
   *
  * Reglas funcionales vigentes:
   * - Tipo de cita debe ser CAMPO (no INFORME).
   * - Debe tener cumplimiento asociado (nmcumplimientoServicio).
   * - Contrato del beneficiario no puede estar en CONTRATOS_CUMPL (contratos excluidos).
  * - Si estadoActa = ERROR y ya existe acta asociada (cdconsecutivoActa),
  *   se ejecuta reintento automático sin modal.
  * - Si no hay acta asociada, abre modal soloActa (requiere correo de contacto).
  * - Si estadoActa está en RECIBIDO 360, EXITOSO, ACTA ASOCIADA o ENVIADO A 360,
  *   el botón debe permanecer deshabilitado.
   *
   * Diferencias con el legado:
   * - No se abre G360 en ventana externa: el backend genera y asocia el acta vía
   *   POST /api/servicios/orientacionesProgramadas/{id}/generar-acta (HU1132419).
   */
  asociarActa(prog: ProgramacionServicio): void {
    // Legacy: validar tipo CAMPO
    if (this.esTipoCitaInforme(prog)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'El cumplimiento no es tipo CAMPO',
        life: 4000
      });
      return;
    }

    // Legacy: validar cumplimiento asociado
    if (!prog.nmcumplimientoServicio) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No hay cumplimiento asociado.',
        life: 4000
      });
      return;
    }

    // Legacy: validar contrato no excluido
    const contratoBeneficiario =
      (prog as ProgramacionServicio & { cdcontratoBeneficiario?: string })
        .cdcontratoBeneficiario || '';
    const contratoBeneficiarioNormalizado = contratoBeneficiario
      .trim()
      .toLowerCase();
    const contratosExcluidos = this.contratosCumplExcluidos();
    if (
      contratoBeneficiarioNormalizado &&
      contratosExcluidos.some(
        (c) => c.trim().toLowerCase() === contratoBeneficiarioNormalizado
      )
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'El cumplimiento no aplica para asociar acta.',
        life: 4000
      });
      return;
    }

    if (this.debeDeshabilitarAsociarActa(prog)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'El acta ya se encuentra asociada al cumplimiento.',
        life: 4000
      });
      return;
    }

    if (this.debeReintentarAutomaticoAsociacionActa(prog)) {
      void this.reintentarAsociacionActaAutomatica(prog);
      return;
    }

    if (!this.debeAbrirModalAsociarActa(prog)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'El acta ya se encuentra asociada al cumplimiento.',
        life: 4000
      });
      return;
    }

    // Validación de email para el flujo de modal (sin acta asociada)
    if (!prog.correoContacto || !prog.correoContacto.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Debe tener un correo electronico para asociar el acta',
        life: 4000
      });
      return;
    }

    // Abrir el form en modo "solo acta" reusando el formulario-cumplimiento
    const programacionPendiente = this.mapearAProgramacionPendiente(prog);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;
    this.programacionParaCumplimiento.set(programacionPendiente);
    this.cumplimientoExistenteAsociar.set(prog);
    this.esTipoCampo.set(true);
    this.esTipoInforme.set(false);
    this.soloActaModo.set(true);
    this.dialogCumplimientoVisible.set(true);
  }

  private async reintentarAsociacionActaAutomatica(
    prog: ProgramacionServicio
  ): Promise<void> {
    if (this.loadingAsociarActa()) {
      return;
    }

    const nmcumplimientoServicio = prog.nmcumplimientoServicio;
    const cdconsecutivoActa = (prog.cdconsecutivoActa || '').trim();

    if (!nmcumplimientoServicio || !cdconsecutivoActa) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error al asociar acta',
        detail: 'No se encontró el consecutivo del acta para reintento automático.'
      });
      return;
    }

    this.loadingAsociarActa.set(true);
    try {
      const result = await this.cumplimientoRepo.asociarActa(
        String(nmcumplimientoServicio),
        cdconsecutivoActa
      );

      if (!result.exito) {
        throw new Error(result.mensaje || 'No se pudo asociar el acta.');
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Acta asociada',
        detail: result.mensaje || 'El acta se asoció correctamente.',
        life: 6000
      });

      this.nmconsecutivoCumplimientoDescargable = nmcumplimientoServicio;
      this.mostrarBotonDescargarActa.set(true);

      const orden = this.ordenSeleccionada();
      if (orden?.nmconsecutivo) {
        await this.cargarProgramaciones(orden.nmconsecutivo);
      }
    } catch (err: unknown) {
      const e = err as {
        error?: { message?: string; detail?: string };
        message?: string;
      };
      const detail =
        e?.error?.detail ||
        e?.error?.message ||
        e?.message ||
        'No fue posible asociar el acta.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error al asociar acta',
        detail
      });
    } finally {
      this.loadingAsociarActa.set(false);
    }
  }

  /**
   * Handler del output `(reintentar)` del formulario-cumplimiento cuando se está en modo soloActa.
   * Reenvía los datos del acta al backend para que genere/asocie el acta en P8 (HU1132419).
   */
  async onReintentarAsociarActa(datosActa: DatosActaRequest): Promise<void> {
    if (this.loadingAsociarActa()) {
      return;
    }

    const prog = this.cumplimientoExistenteAsociar();
    const nmcumplimientoServicio = prog?.nmcumplimientoServicio;
    if (!nmcumplimientoServicio) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se encontró el consecutivo del cumplimiento.'
      });
      return;
    }

    this.loadingAsociarActa.set(true);
    try {
      const result = await this.cumplimientoRepo.reintentarGenerarActa(
        nmcumplimientoServicio,
        datosActa
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Acta asociada',
        detail: `El acta ${result.idActa360} fue generada y asociada al cumplimiento.`,
        life: 6000
      });
      this.nmconsecutivoCumplimientoDescargable = nmcumplimientoServicio;
      this.mostrarBotonDescargarActa.set(true);

      const orden = this.ordenSeleccionada();
      if (orden?.nmconsecutivo) {
        await this.cargarProgramaciones(orden.nmconsecutivo);
      }
    } catch (err: unknown) {
      const e = err as {
        error?: { message?: string; detail?: string };
        message?: string;
      };
      const detail =
        e?.error?.detail ||
        e?.error?.message ||
        e?.message ||
        'No fue posible asociar el acta.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error al asociar acta',
        detail
      });
    } finally {
      this.loadingAsociarActa.set(false);
    }
  }

  // === Ingresar Cumplimiento ===

  /**
   * Abre el modal de cumplimiento para la programación seleccionada.
   * Mapea ProgramacionServicio -> ProgramacionPendiente y consulta el saldo actualizado.
   *
   * @param prog Programación seleccionada del listado
   */
  async ingresarCumplimiento(prog: ProgramacionServicio): Promise<void> {
    console.log(
      '[IngresarCumplimiento] Abriendo modal para programación:',
      prog.nmconsecutivo
    );

    // Legacy: isContratoExentoActa() lee CONTRATOS_CUMPL ya cargado al iniciar la aplicación.
    // Aquí la carga es asíncrona: se espera antes de abrir para no mostrar el panel de acta
    // a un contrato exento.
    if (this.contratosCumplExcluidos().length === 0) {
      await this.cargarContratosCumplExcluidos();
    }

    // Mapear ProgramacionServicio -> ProgramacionPendiente
    const programacionPendiente = this.mapearAProgramacionPendiente(prog);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;

    // Setear la programación seleccionada
    this.programacionParaCumplimiento.set(programacionPendiente);

    // Determinar tipo de cita usando lógica robusta (texto + código)
    // Legacy: CumplimientosFacade.esTipoCampo() - checa ambos
    const esCampo = esTipoCitaCampo(prog);
    const esInforme = esTipoCitaInforme(prog);

    this.esTipoCampo.set(esCampo);
    this.esTipoInforme.set(esInforme);

    console.log(
      '[IngresarCumplimiento] dsTipoCita:',
      prog.dstipoCita,
      'código:',
      prog.nmconsecutivoTipoCita,
      'esCampo:',
      esCampo,
      'esInforme:',
      esInforme
    );

    // Consultar saldo actualizado si hay nmconsecOrdenServDetalle
    if (prog.nmconsecOrdenServDetalle) {
      try {
        this.loadingCumplimiento.set(true);
        const saldoResponse = await this.cumplimientoRepo.obtenerSaldoOrden(
          prog.nmconsecOrdenServDetalle
        );
        this.saldoCumplimiento.set(saldoResponse.saldoPorEjecutar || 0);
        console.log(
          '[IngresarCumplimiento] Saldo actualizado:',
          this.saldoCumplimiento()
        );
      } catch (error) {
        console.error('[IngresarCumplimiento] Error consultando saldo:', error);
        // Usar saldo de la programación como fallback
        this.saldoCumplimiento.set(programacionPendiente.saldoPorEjecutar || 0);
      } finally {
        this.loadingCumplimiento.set(false);
      }
    } else {
      this.saldoCumplimiento.set(programacionPendiente.saldoPorEjecutar || 0);
    }

    this.requiereJustificacion.set(false);
    void this.cargarJustificacionesCumplimientoTardio();

    // Precargar municipios de transporte (vienen directamente en la programación desde TPSC_ORDEN_SERVICIO_DETAL)
    console.log('[IngresarCumplimiento] Municipios de la programación:', {
      cdmunicipioOrigen: prog.cdmunicipioOrigen,
      dsmunicipioOrigen: prog.dsmunicipioOrigen,
      cdsuraOrigen: prog.cdsuraOrigen,
      cdmunicipioEntrega: prog.cdmunicipioEntrega,
      dsmunicipioEntrega: prog.dsmunicipioEntrega,
      cdsuraEntrega: prog.cdsuraEntrega
    });

    if (prog.cdmunicipioOrigen || prog.dsmunicipioOrigen) {
      this.municipioOrigenInicialCumplimiento.set({
        cdmunicipio: prog.cdmunicipioOrigen,
        cdsura: prog.cdsuraOrigen,
        dsmunicipio: prog.dsmunicipioOrigen
      });
    } else {
      this.municipioOrigenInicialCumplimiento.set(null);
    }

    if (prog.cdmunicipioEntrega || prog.dsmunicipioEntrega) {
      this.municipioEntregaInicialCumplimiento.set({
        cdmunicipio: prog.cdmunicipioEntrega,
        cdsura: prog.cdsuraEntrega,
        dsmunicipio: prog.dsmunicipioEntrega
      });
    } else {
      this.municipioEntregaInicialCumplimiento.set(null);
    }

    // Abrir el dialog
    this.dialogCumplimientoVisible.set(true);
  }

  /**
   * Carga el catálogo de justificaciones para el modal de cumplimiento tardío.
   * Mismo catálogo (TPSC_JUSTIFICACIONES) consumido por CumplimientosProgramadosFacade.
   */
  private async cargarJustificacionesCumplimientoTardio(): Promise<void> {
    try {
      const opciones = await this.catalogoRepository.getJustificacionesCancelacion();
      this.justificaciones.set(
        opciones.map((opcion) => ({
          label: opcion.descripcion,
          value: opcion.codigo
        }))
      );
    } catch (error) {
      console.error(
        '[IngresarCumplimiento] Error cargando justificaciones de cumplimiento tardío:',
        error
      );
      this.justificaciones.set([]);
    }
  }

  /**
   * Mapea ProgramacionServicio (del listado de programaciones) a ProgramacionPendiente
   * (formato requerido por FormularioCumplimientoComponent).
   */
  private mapearAProgramacionPendiente(
    prog: ProgramacionServicio
  ): ProgramacionPendiente {
    const detalle = this.detalle();
    const orden = this.ordenSeleccionada();

    return {
      nmconsecutivo: prog.nmconsecutivo,
      nmconsecOrdenServDetalle:
        prog.nmconsecOrdenServDetalle || detalle?.nmconsecutivo || 0,
      nmconsecutivoManualOrden:
        detalle?.codigoOrden || orden?.nmconsecutivoManualOrden || '',
      nmconsecutivoTipoCita: prog.nmconsecutivoTipoCita,
      dstipoCita: prog.dstipoCita,
      dsnombreAseguradora: this.nombreAseguradora() || detalle?.aseguradora,
      dsnombreCliente: detalle?.nombreBeneficiario,
      dsnombreBeneficiario: detalle?.nombreBeneficiario,
      nmconsecutivoBeneficiario: detalle?.nmconsecutivoBeneficiario,
      dsnombreContacto: prog.nombreContacto,
      dsemailContacto: prog.correoContacto,
      dscentroTrabajo: detalle?.centroTrabajo,
      dstarea: detalle?.tareaCliente,
      dsnombreServicio: detalle?.servicioCgr,
      feprogramacion: prog.fechaProgramacion,
      horaInicial: prog.fechaProgramacion?.split('T')[1],
      nmhorasCita: prog.horasCita,
      nmcantidadProgramada: prog.cantidadProgramada,
      saldoPorEjecutar:
        this.saldoCumplimiento() || detalle?.saldoPorEjecutar || 0,
      cdestado: prog.cdestado,
      dsestado: prog.estado,
      // Clasificación y contrato -> HU1132419: necesarios para renderizar el panel "Asociación de Acta"
      // Legacy: CreateOrUpdateCumplimientoViewImpl.validarVisibilidadCamposActas()
      //   usa dto.getDsclasificacion() y dto.getNmconsecutivo_beneficiario().getCdcontrato()
      dsclasificacion:
        prog.dsclasificacion ||
        (detalle as { dsclasificacion?: string } | null)?.dsclasificacion,
      cdcontratoBeneficiario: prog.cdcontratoBeneficiario,
      // Campos de transporte - vienen directamente en la programación desde TPSC_ORDEN_SERVICIO_DETAL
      cdmunicipioOrigen: prog.cdmunicipioOrigen,
      dsmunicipioOrigen: prog.dsmunicipioOrigen,
      cdsuraOrigen: prog.cdsuraOrigen,
      cdmunicipioEntrega: prog.cdmunicipioEntrega,
      dsmunicipioEntrega: prog.dsmunicipioEntrega,
      cdsuraEntrega: prog.cdsuraEntrega,
      // Oficina/cliente de la orden: sin ellos el backend no puede aplicar el
      // respaldo de TPSC_VAL_TTE_PERIMETRO y el valor de transporte quedaba en 0.
      // Legacy: getOficinaOrden()/getCliente() → AdminValorTransporteHandler
      nmconsecutivoOficina: detalle?.nmconsecutivoOficina,
      nmconsecutivoCliente: detalle?.nmconsecutivoCliente
    };
  }

  /**
   * Maneja el cambio de visibilidad del dialog de cumplimiento.
   */
  onDialogCumplimientoVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrarDialogCumplimiento();
    }
  }

  /**
   * Maneja el guardado del cumplimiento.
   * Delega al repository y refresca la lista de programaciones.
   */
  async onGuardarCumplimiento(request: CumplimientoRequest): Promise<void> {
    if (!environment.production) {
      console.log(
        '[IngresarCumplimiento] Guardando cumplimiento, programacion/detalle:',
        request.nmconsecutivoProgramacion ?? request.nmconsecOrdenServDetalle
      );
    }
    this.loadingCumplimiento.set(true);

    try {
      const response =
        await this.cumplimientoRepo.registrarCumplimiento(request);
      avisarPostPersistencia(this.messageService, response);
      const contactosInformePendiente = gestionContactosInformeSiAplica(request, response);
      if (contactosInformePendiente) {
        void this.contactosInformeDialog?.abrir(
          contactosInformePendiente.radicado,
          contactosInformePendiente.cumplimientos
        );
      }

      // Si retorna un nmconsecutivo, el guardado fue exitoso
      if (response?.nmconsecutivo) {
        this.messageService.add({
          severity: 'success',
          summary: 'Cumplimiento Registrado',
          detail: 'El cumplimiento se ha registrado correctamente'
        });
        if (response.idActa360) {
          this.nmconsecutivoCumplimientoDescargable = response.nmconsecutivo;
          this.mostrarBotonDescargarActa.set(true);
          this.messageService.add({
            severity: 'success',
            summary: 'Acta asociada',
            detail: `El acta ${response.idActa360} fue generada y asociada al cumplimiento.`
          });

          const orden = this.ordenSeleccionada();
          if (orden?.nmconsecutivo) {
            await this.cargarProgramaciones(orden.nmconsecutivo);
          }
          return;
        }

        // Cerrar dialog
        this.cerrarDialogCumplimiento();

        // Refrescar lista de programaciones
        const orden = this.ordenSeleccionada();
        if (orden?.nmconsecutivo) {
          this.cargarProgramaciones(orden.nmconsecutivo);
        }
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo registrar el cumplimiento'
        });
      }
    } catch (error: any) {
      console.error(
        '[IngresarCumplimiento] Error guardando cumplimiento:',
        error
      );
      const detalle =
        error?.error?.message ??
        error?.error?.detail ??
        error?.message ??
        'Ocurrió un error al registrar el cumplimiento';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: detalle,
        life: 8000
      });
    } finally {
      this.loadingCumplimiento.set(false);
    }
  }

  /**
  * Maneja el cambio de fecha de cumplimiento para verificar si es tardío.
  * Por ahora se deja vacío - se implementará si es necesario.
   */
  onFechaCumplimientoChange(fecha: Date): void {
    console.log('[IngresarCumplimiento] Fecha cumplimiento cambió:', fecha);
    // TODO: Verificar si requiere justificación por ser tardío
    // Por ahora se delega al formulario de cumplimiento
  }

  async reportarAlertaTemprana(): Promise<void> {
    await abrirAlertaTemprana(
      this.programacionParaCumplimiento(),
      this.messageService
    );
  }

  /**
   * Maneja la solicitud de ver historial de cumplimientos.
   * Carga el historial desde el backend y muestra el modal.
   */
  async onVerHistorialCumplimientos(): Promise<void> {
    const prog = this.programacionParaCumplimiento();
    if (!prog?.nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin programación',
        detail: 'No hay programación seleccionada'
      });
      return;
    }

    try {
      this.loadingHistorial.set(true);
      const cumplimientos =
        await this.cumplimientoRepo.listarHistorialCumplimientos(
          prog.nmconsecutivo
        );
      this.historialCumplimientos.set(cumplimientos);
      this.historialCumplimientosVisible.set(true);
    } catch (error) {
      console.error('[ProgramarServicio] Error cargando historial:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el historial de cumplimientos'
      });
    } finally {
      this.loadingHistorial.set(false);
    }
  }

  /**
   * Cierra el dialog de cumplimiento y limpia el estado.
   */
  cerrarDialogCumplimiento(): void {
    this.dialogCumplimientoVisible.set(false);
    this.programacionParaCumplimiento.set(null);
    this.saldoCumplimiento.set(0);
    this.loadingCumplimiento.set(false);
    this.esTipoCampo.set(false);
    this.esTipoInforme.set(false);
    this.requiereJustificacion.set(false);
    this.justificaciones.set([]);
    this.municipioOrigenInicialCumplimiento.set(null);
    this.municipioEntregaInicialCumplimiento.set(null);
    this.soloActaModo.set(false);
    this.cumplimientoExistenteAsociar.set(null);
    this.loadingAsociarActa.set(false);
    this.mostrarBotonDescargarActa.set(false);
    this.descargandoActaAsociada.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;
  }

  async onDescargarActaAsociada(): Promise<void> {
    const nmconsecutivo = this.nmconsecutivoCumplimientoDescargable;
    if (!nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin documento',
        detail: 'No hay un acta asociada disponible para descargar'
      });
      return;
    }

    this.descargandoActaAsociada.set(true);
    try {
      await this.cumplimientoRepo.descargarActaPdf(
        nmconsecutivo,
        `acta_${nmconsecutivo}.pdf`
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Descarga iniciada',
        detail: 'El documento se está descargando'
      });
    } catch (error) {
      const mensaje =
        error instanceof Error
          ? error.message
          : 'Error al descargar el documento';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: mensaje
      });
    } finally {
      this.descargandoActaAsociada.set(false);
    }
  }

  // === Cancelar Programación ===

  /**
   * Abre el modal de cancelación para la programación seleccionada.
   */
  cancelarProgramacion(prog: ProgramacionServicio): void {
    console.log('[Cancelar] Programación seleccionada:', {
      id: prog.nmconsecutivo,
      snaplicaCliente: prog.snaplicaCliente,
      tipoIdAsesor: prog.tipoIdAsesor,
      numeroIdAsesor: prog.numeroIdAsesor,
      nombre1Asesor: prog.nombre1Asesor,
      nombreAsesor: prog.nombreAsesor
    });
    console.log(
      '[Cancelar] Datos adaptados:',
      this.adaptarProgramacionParaCancelacion(prog)
    );
    this.programacionACancelar.set(prog);
    this.mostrarCancelarProgramacion.set(true);
  }

  /**
   * Cierra el modal de cancelación sin procesar.
   */
  cerrarModalCancelacion(): void {
    this.mostrarCancelarProgramacion.set(false);
    this.programacionACancelar.set(null);
  }

  /**
   * Adapta ProgramacionServicio de orden-servicio al formato esperado por CancelarCitaDialogComponent.
   * El componente de cancelación espera un objeto con la estructura de agenda.
   */
  adaptarProgramacionParaCancelacion(prog: ProgramacionServicio): any {
    return {
      // ID de la cita
      nmconsecutivoProg: prog.nmconsecutivo,

      // Datos del tipo de cita (para filtrar tipos de cancelación)
      snaplicaCliente: prog.snaplicaCliente || 'N',

      // Datos del asesor (campos individuales para el panel de persona)
      nombreAsesor: prog.nombreAsesor,
      dstipoIdentificacionAsesor: getTipoIdentificacionDescripcion(
        prog.tipoIdAsesor
      ),
      nmidentificacionAsesor: prog.numeroIdAsesor,
      dsnombre1Asesor: prog.nombre1Asesor,
      dsnombre2Asesor: prog.nombre2Asesor,
      dsapellido1Asesor: prog.apellido1Asesor,
      dsapellido2Asesor: prog.apellido2Asesor,

      // Datos del cliente (para pre-poblar si cancela el cliente)
      nombreBeneficiario:
        prog.nombreComercialCliente ||
        [prog.nombre1Cliente, prog.apellido1Cliente].filter(Boolean).join(' '),
      dsidentificacionBeneficiario: prog.nmidentificacionCliente,
      dstipoIdentificacionBeneficiario: getTipoIdentificacionDescripcion(
        prog.tipoIdCliente
      ),

      // Estado
      dsestado: prog.estado,
      cdestado: prog.cdestado
    };
  }

  /**
   * Obtiene los datos del usuario en sesión (programador).
   * Los datos vienen de UsuarioStateService que carga desde el backend.
   */
  datosProgramadorSesion(): DatosPersona | null {
    const usuario = this.usuarioState.usuario();
    if (!usuario) {
      return null;
    }

    return {
      tipoIdentificacion: getTipoIdentificacionDescripcion(
        usuario.tipoIdentificacion
      ),
      numeroIdentificacion: usuario.numeroIdentificacion,
      nombre1: usuario.primerNombre,
      nombre2: usuario.segundoNombre,
      apellido1: usuario.primerApellido,
      apellido2: usuario.segundoApellido,
      nombreCompleto: usuario.nombreCompleto
    };
  }

  /**
  * Procesa la confirmación de cancelación desde el modal.
   * Llama al SP de cancelación y recarga las programaciones.
   */
  async onCancelacionConfirmada(resultado: CancelarCitaResult): Promise<void> {
    this.cancelandoProgramacion.set(true);

    try {
      const prog = this.programacionACancelar();
      const orden = this.ordenSeleccionada();

      if (!prog || !orden) {
        throw new Error('No hay programación o orden seleccionada');
      }

      console.log('[Cancelar] Resultado del modal:', resultado);

      // Llamar al SP de cancelación (usando AgendaRepository)
      await this.agendaRepository.cancelarCitaSP({
        citaId: resultado.citaId,
        codigoTipoCancelador: resultado.codigoTipoCancelador,
        descripcionTipoCancelador: resultado.descripcionTipoCancelador,
        nombreContacto: resultado.nombreContacto,
        observaciones: resultado.observaciones,
        consecutivoUsuario: this.usuarioState.usuario()?.nmconsecutivo,
        consecutivoOrden: orden.nmconsecutivo,
        cancelarFuturas: resultado.cancelarFuturas || 'N',
        codigoJustificacion: resultado.codigoJustificacion,
        descripcionJustificacion: resultado.descripcionJustificacion,
        motivoCancelacion: resultado.motivoCancelacion
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Cancelación exitosa',
        detail: `La programación ${resultado.citaId} ha sido cancelada correctamente`
      });

      // Cerrar modal y recargar programaciones
      this.cerrarModalCancelacion();
      this.cargarProgramaciones(orden.nmconsecutivo);
    } catch (error) {
      console.error('[Cancelar] Error cancelando programación:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cancelar la programación. Intente nuevamente.'
      });
    } finally {
      this.cancelandoProgramacion.set(false);
    }
  }

  /**
   * Determina si hay un acta/documento disponible para descargar.
   *
   * No filtra por estadoActa: legacy (ProgramarServiciosActivity.descargarActa360())
   * solo verifica existencia del acta (cdconsecutivo_acta / idActa360), no su estado.
   */
  tieneActaDescargable(prog: ProgramacionServicio): boolean {
    return !!(
      prog.cdidP8Preliminar ||
      prog.cdconsecutivoActa ||
      prog.idActa360
    );
  }

  /**
   * Descarga el acta asociada a la programación.
   *
   * Legacy: ProgramarServiciosActivity.descargarActa360() / consumirServicio()
  * - Si el acta ya quedó almacenada en P8 (cdidP8Preliminar) se descarga directo desde P8.
  * - Si aún no hay documento en P8, se usa el mismo flujo G360 del legacy: se prioriza
   *   cdconsecutivoActa (acta permanente) y, si no existe, el id temporal idActa360
   *   (NMACTA360_TEMP), reutilizando el endpoint GET /api/cumplimientos/{id}/descargar-acta
   *   (que ya implementa esa misma prioridad en el backend).
   *
  * La experiencia de descarga es idéntica sin importar el origen (P8 o G360):
   * se obtiene el binario vía blob y se dispara la descarga en el navegador
  * (nunca se abre una pestaña/ventana externa con la URL).
   *
  * No se filtra por estadoActa: se habilita únicamente por existencia del documento.
   */
  async descargarActa(prog: ProgramacionServicio): Promise<void> {
    if (!this.tieneActaDescargable(prog)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin documento',
        detail: 'Esta programación no tiene un acta disponible para descargar'
      });
      return;
    }

    this.descargandoActa.set(prog.nmconsecutivo);

    try {
      if (prog.cdidP8Preliminar) {
        await this.documentoP8Service.descargar(
          prog.cdidP8Preliminar,
          `acta_${prog.nmconsecutivo}.pdf`
        );
      } else if (prog.nmcumplimientoServicio) {
        await this.cumplimientoRepo.descargarActaPdf(
          prog.nmcumplimientoServicio,
          `acta_${prog.nmconsecutivo}.pdf`
        );
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Sin documento',
          detail:
            'Esta programación no tiene un acta disponible para descargar'
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Descarga iniciada',
        detail: 'El documento se está descargando'
      });
    } catch (error) {
      const mensaje =
        error instanceof Error
          ? error.message
          : 'Error al descargar el documento';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: mensaje
      });
    } finally {
      this.descargandoActa.set(null);
    }
  }

  verObservaciones(prog: ProgramacionServicio): void {
    this.programacionObservaciones.set(prog);
    this.mostrarObservaciones.set(true);
  }

  programarOtraTarea(tarea: TareaBeneficiario): void {
    this.onCambiarOrden.emit(tarea);
  }

  verHistorialGestiones(): void {
    this.mostrarHistorialGestiones.set(true);
  }

  cerrarHistorialGestiones(): void {
    this.mostrarHistorialGestiones.set(false);
  }

  /**
   * Muestra el diálogo de contactos del centro de trabajo o beneficiario.
   *
   * Legacy: AdminContactosHandler.listarContactosBenefOrden()
   * - Si tiene centro de trabajo -> usar nmconsecCentroTrabajo
   * - Si no tiene centro de trabajo -> usar cdpersBeneficiario
   */
  async verContactos(): Promise<void> {
    if (!this.detalle()) return;
    if (await this.consultarContactos(() => this.contactosPager.cargarPrimera())) {
      this.mostrarContactos.set(true);
    }
  }

  /** Repite la consulta de la página actual (tras crear, modificar o eliminar un contacto). */
  private async recargarContactos(): Promise<void> {
    await this.consultarContactos(() => this.contactosPager.recargar());
  }

  /** Cambia de página: cada acción es una consulta real al backend con el cursor vigente. */
  async onPaginarContactos(accion: AccionPaginacionContactos): Promise<void> {
    await this.consultarContactos(() => this.contactosPager.paginar(accion));
  }

  private async consultarContactos(accion: () => Promise<void>): Promise<boolean> {
    this.loadingContactos.set(true);
    try {
      await accion();
      return true;
    } catch (error) {
      if (!environment.production) {
        console.error('Error al cargar contactos:', error);
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los contactos'
      });
      return false;
    } finally {
      this.loadingContactos.set(false);
    }
  }

  /**
   * Cierra el diálogo de contactos.
   */
  cerrarContactos(): void {
    this.mostrarContactos.set(false);
    this.contactosPager.vaciar();
  }

  /**
  * Maneja la creación de un nuevo contacto.
   */
  async onContactoCreado(contacto: ContactoCentroTrabajo): Promise<void> {
    const det = this.detalle();
    if (!det?.cdpersBeneficiario) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede crear contacto sin persona asociada'
      });
      return;
    }

    const cdpersona = det.cdpersBeneficiario;

    try {
      await this.contactoPersonaRepo.create(cdpersona, {
        cdpersona: cdpersona,
        dsnombre: contacto.dsnombre,
        dscargo: contacto.dscargo,
        dstelefono: contacto.dstelefonoFijo,
        dscelular: contacto.dstelefonoCelular,
        dsemail: contacto.dsemail,
        // Si hay centro de trabajo, asociar el contacto (inserta en TPSC_CONTAC_CENTRO_TRAB)
        nmconsecCentroTrabajo: det.nmconsecCentroTrabajo
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se almacenaron correctamente.'
      });

      // Recargar lista de contactos
      await this.recargarContactos();
    } catch (error) {
      if (!environment.production) {
        console.error('Error al crear contacto:', error);
      }
      this.errorGuardadoContactos.update((n) => n + 1);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: esPersonaNoEncontrada(error)
          ? MENSAJE_PERSONA_DESACTUALIZADA
          : this.obtenerMensajeErrorGuardado(error, 'No se pudo crear el contacto')
      });
    }
  }

  /**
  * Maneja la actualización de un contacto existente.
   */
  async onContactoModificado(contacto: ContactoCentroTrabajo): Promise<void> {
    const det = this.detalle();
    if (!det?.cdpersBeneficiario || !contacto.nmconsecutivo) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede modificar el contacto'
      });
      return;
    }

    const cdpersona = contacto.cdpersona ?? det.cdpersBeneficiario;

    try {
      await this.contactoPersonaRepo.update(cdpersona, contacto.nmconsecutivo, {
        cdpersona: cdpersona,
        dsnombre: contacto.dsnombre,
        dscargo: contacto.dscargo,
        dstelefono: contacto.dstelefonoFijo,
        dscelular: contacto.dstelefonoCelular,
        dsemail: contacto.dsemail
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se actualizaron correctamente.'
      });

      // Recargar lista de contactos
      await this.recargarContactos();
    } catch (error) {
      if (!environment.production) {
        console.error('Error al actualizar contacto:', error);
      }
      this.errorGuardadoContactos.update((n) => n + 1);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: esPersonaNoEncontrada(error)
          ? MENSAJE_PERSONA_DESACTUALIZADA
          : this.obtenerMensajeErrorGuardado(error, 'No se pudo actualizar el contacto')
      });
    }
  }

  private obtenerMensajeErrorGuardado(
    error: unknown,
    fallback: string
  ): string {
    const anyError = error as any;
    const cuerpo = anyError?.error;
    const errorAnidado = cuerpo?.error;

    // `detail` (mensaje seguro del contrato ErrorResponse) antes que `message` (categoría genérica).
    // El `message` de un HttpErrorResponse ("Http failure response for ...") es técnico: no se muestra.
    return (
      errorAnidado?.detail ||
      errorAnidado?.message ||
      (Array.isArray(cuerpo?.errors) ? cuerpo.errors[0]?.message : undefined) ||
      cuerpo?.detail ||
      cuerpo?.message ||
      (error instanceof HttpErrorResponse ? undefined : anyError?.message) ||
      fallback
    );
  }

  /**
  * Maneja la eliminación (inactivación) de un contacto.
   * Inactiva en TPSC_PERSONAS_CONTACTOS y TPSC_CONTAC_CENTRO_TRAB.
   */
  async onContactoEliminado(contacto: ContactoCentroTrabajo): Promise<void> {
    const det = this.detalle();
    if (!det?.cdpersBeneficiario || !contacto.nmconsecutivo) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede eliminar el contacto'
      });
      return;
    }

    // El backend exige que el contacto pertenezca a la persona de la ruta (o esté vinculado al centro de
    // trabajo): se usa el dueño real del contacto, igual que en modificar.
    const cdpersona = contacto.cdpersona ?? det.cdpersBeneficiario;
    const nmconsecCentroTrabajo = det.nmconsecCentroTrabajo;

    try {
      if (nmconsecCentroTrabajo) {
        // Legacy ListaContactosViewImpl: baja lógica del contacto anulando rol y cargo
        await this.contactoPersonaRepo.deleteWithCentroTrabajo(
          cdpersona,
          contacto.nmconsecutivo,
          nmconsecCentroTrabajo
        );
      } else {
        // Sin centro de trabajo: misma baja del popup (legacy anula rol y cargo)
        await this.contactoPersonaRepo.delete(
          cdpersona,
          contacto.nmconsecutivo,
          true
        );
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se eliminaron correctamente.'
      });

      // Recargar lista de contactos
      await this.recargarContactos();
    } catch (error) {
      if (!environment.production) {
        console.error('Error al eliminar contacto:', error);
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          (error as any)?.error?.detail ||
          (error as any)?.error?.message ||
          'No se pudo eliminar el contacto'
      });
    }
  }

  cerrar(): void {
    this.onClose.emit();
  }

  abrirInformacionEmpresaDesdeDetalle(): void {
    const detalle = this.detalle();
    const orden = this.ordenSeleccionada();

    const nmconsecutivoBeneficiario =
      detalle?.nmconsecutivoBeneficiario ?? orden?.nmconsecutivoBeneficiario;

    if (!nmconsecutivoBeneficiario || nmconsecutivoBeneficiario <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Información Empresa',
        detail:
          'No fue posible identificar el cliente para consultar su información.'
      });
      return;
    }

    const queryParams: Record<string, string | number> = {
      nmconsecutivoBeneficiario
    };

    if (detalle?.nombreBeneficiario) {
      queryParams['nombreBeneficiario'] = detalle.nombreBeneficiario;
    }
    if (orden?.tipoIdBeneficiario) {
      queryParams['tipoIdentificacionBeneficiario'] = orden.tipoIdBeneficiario;
    }
    if (orden?.nmIdBeneficiario) {
      queryParams['numeroIdentificacionBeneficiario'] = orden.nmIdBeneficiario;
    }

    void this.router.navigate(['/informacion-empresa'], { queryParams });
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrar();
    }
  }

  // === Helpers de formato ===

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    try {
      // Parsear YYYY-MM-DD como fecha local para evitar el desfase por zona horaria UTC
      const parts = String(dateStr).split('T')[0].split('-');
      const date = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2])
      );
      return date.toLocaleDateString('es-CO');
    } catch {
      return dateStr;
    }
  }

  formatDateTime(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return dateStr;
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  getEstadoSeverity(
    cdestado: string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (cdestado?.toUpperCase()) {
      case 'APR':
      case 'APROBADA':
        return 'success';
      case 'PEN':
      case 'PENDIENTE':
        return 'warn';
      case 'CAN':
      case 'CANCELADA':
        return 'danger';
      case 'EJE':
      case 'EJECUTADA':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getEstadoProgSeverity(
    cdestado: string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (cdestado?.toUpperCase()) {
      case 'APR':
      case 'APROBADA':
        return 'success';
      case 'REP':
      case 'REPROGRAMADA':
        return 'info';
      case 'CAN':
      case 'CANCELADA':
        return 'danger';
      case 'CUM':
      case 'CUMPLIDA':
        return 'success';
      default:
        return 'secondary';
    }
  }

  /**
   * Determina si el tipo de cita es INFORME.
   * El SP devuelve el nombre de imagen: "appointmentTypeReport.jpg" para INFORME,
   * "appointmentTypeDate.jpg" para CAMPO.
   * También puede venir como texto "Informe" o "Campo", o número (18=INFORME, 17=CAMPO).
   */
  esTipoCitaInforme(prog: ProgramacionServicio): boolean {
    // Verificar todos los campos posibles
    const tipoCita = (
      prog.dstipoCita ||
      prog.cdtipoCita ||
      prog.imagenTipoCita ||
      ''
    ).toLowerCase();

    // Verificar por nombre de imagen (appointmentTypeReport.jpg)
    if (tipoCita.includes('report')) {
      return true;
    }
    // Verificar por texto descriptivo
    if (tipoCita.includes('informe')) {
      return true;
    }
    // Verificar por número (18 = INFORME, 43 = INFORME_COMPLEMENTARIO)
    const numTipoCita = prog.nmconsecutivoTipoCita;
    if (numTipoCita === 18 || numTipoCita === 43) {
      return true;
    }
    return false;
  }

  mostrarBotonAsociarActa(prog: ProgramacionServicio): boolean {
    return !this.esTipoCitaInforme(prog) && !!prog.nmcumplimientoServicio;
  }

  debeDeshabilitarAsociarActa(prog: ProgramacionServicio): boolean {
    const estadoActa = this.normalizarEstadoActa(prog);
    return (
      estadoActa === 'RECIBIDO 360' ||
      estadoActa === 'EXITOSO' ||
      estadoActa === 'ACTA ASOCIADA' ||
      estadoActa === 'ENVIADO A 360'
    );
  }

  private debeReintentarAutomaticoAsociacionActa(
    prog: ProgramacionServicio
  ): boolean {
    return (
      this.normalizarEstadoActa(prog) === 'ERROR' &&
      this.tieneActaAsociada(prog)
    );
  }

  private debeAbrirModalAsociarActa(prog: ProgramacionServicio): boolean {
    return !this.tieneActaAsociada(prog);
  }

  private tieneActaAsociada(prog: ProgramacionServicio): boolean {
    return !!(prog.cdconsecutivoActa || '').trim();
  }

  private normalizarEstadoActa(prog: ProgramacionServicio): string {
    return (prog.estadoActa || '').trim().toUpperCase();
  }

  /**
   * Determina si se puede mostrar el botón de Asociar Acta para una programación.
   *
   * Legacy: ProgramarServiciosViewImpl.setupAcciones()
   *
   * Condiciones para mostrar el botón:
   * - Tipo de cita debe ser CAMPO (17), NO INFORME
   * - Debe tener cumplimiento asociado (nmcumplimientoServicio != null)
   * - Estado del acta no debe ser 'ACTA ASOCIADA' ni 'EXITOSO'
  * - El estado debe ser vacío o 'ERROR' para permitir asociación/re-asociación.
   * - Si el estado es 'RECIBIDO 360' se deshabilita la acción.
   */
  puedeAsociarActa(prog: ProgramacionServicio): boolean {
    // Solo aplica para citas tipo CAMPO (no INFORME)
    if (this.esTipoCitaInforme(prog)) {
      return false;
    }

    // Debe tener cumplimiento
    if (!prog.nmcumplimientoServicio) {
      return false;
    }

    // El estado del acta no debe estar en lista de estados bloqueados
    if (this.debeDeshabilitarAsociarActa(prog)) {
      return false;
    }

    // Solo mostrar si el estado es 'ERROR' o está vacío
    // (permite re-asociar en caso de error o primera asociación)
    const estadoActa = this.normalizarEstadoActa(prog);
    return estadoActa === '' || estadoActa === 'ERROR';
  }

  /**
   * Determina si el acta ya está asociada para mostrar el botón de refrescar.
   */
  esActaAsociada(prog: ProgramacionServicio): boolean {
    const estadoActa = (prog.estadoActa || '').toUpperCase();
    return estadoActa === 'ACTA ASOCIADA';
  }

  /**
   * Determina si el acta está en un estado intermedio donde el usuario debe
   * refrescar manualmente para ver el estado final ('ENVIADO A 360' o 'RECIBIDO 360').
  * Usado únicamente para mostrar el botón "Refrescar Estado"; ya NO condiciona
   * la disponibilidad de "Descargar Acta" (ver tieneActaDescargable()).
   */
  esActaEnviadaA360(prog: ProgramacionServicio): boolean {
    const estadoActa = (prog.estadoActa || '').toUpperCase();
    return estadoActa === 'ENVIADO A 360' || estadoActa === 'RECIBIDO 360';
  }

  /**
   * Determina si la cita está en estado CANCELADA.
   * Cuando está cancelada, todas las acciones deben estar deshabilitadas.
   *
   * Legacy: ESTADO_CITA.CANCELADA = '0017'
   */
  esCitaCancelada(prog: ProgramacionServicio): boolean {
    return prog.cdestado === ESTADO_CITA.CANCELADA;
  }

  /**
   * Determina si la cita está en estado EJECUTADA.
   * Cuando está ejecutada, la acción de asociar acta debe estar deshabilitada.
   *
   * Legacy: ESTADO_CITA.EJECUTADA = '0016'
   */
  esCitaEjecutada(prog: ProgramacionServicio): boolean {
    return prog.cdestado === ESTADO_CITA.EJECUTADA;
  }

  /**
   * Refresca el listado de programaciones para actualizar el estado del acta.
   */
  async refrescarProgramaciones(): Promise<void> {
    const detalle = this.detalle();
    if (detalle?.nmconsecutivo) {
      await this.cargarProgramaciones(detalle.nmconsecutivo);
      this.messageService.add({
        severity: 'info',
        summary: 'Actualizado',
        detail: 'Listado de programaciones actualizado'
      });
    }
  }

  /**
   * Carga el detalle de otra tarea del mismo beneficiario.
  * Recarga el modal con la información de la orden seleccionada.
   *
   * Valida primero si la orden está bloqueada por otro usuario usando
  * la función FN_SNTAREA_BLOQ_PARA_USR.
   *
   * @param tarea La tarea del beneficiario a detallar
   */
  async detallarTarea(tarea: TareaBeneficiario): Promise<void> {
    if (tarea.esOrdenActual) {
      return; // No hacer nada si es la orden actual
    }

    // Usar el nmconsecutivo de la tarea (que es el ID del detalle de orden)
    const nmconsecutivo = tarea.nmconsecutivo;
    const cdUsuario = this.usuarioState.usuario()?.nmconsecutivo;

    // Validar bloqueo antes de cargar
    if (nmconsecutivo && cdUsuario) {
      try {
        const estaBloqueada =
          await this.bloqueoProgramacionRepo.verificarBloqueo(
            nmconsecutivo,
            cdUsuario
          );

        if (estaBloqueada) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Orden bloqueada',
            detail:
              'Esta orden está siendo programada por otro usuario. Por el momento no es posible realizar la acción.'
          });
          return;
        }
      } catch (error) {
        console.error('Error verificando bloqueo:', error);
        // Si falla la verificación, permitir continuar pero loguear el error
      }
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Cargando',
      detail: `Cargando detalle de orden ${tarea.codigoOrden || nmconsecutivo}...`
    });

    // Recargar el modal con la nueva orden usando un objeto parcial
    const ordenParcial = {
      nmconsecutivo: nmconsecutivo,
      nmconsecutivoBeneficiario:
        this.ordenSeleccionada()?.nmconsecutivoBeneficiario
    } as OrdenServicio;

    await this.cargarDatos(ordenParcial);
  }

  // === Métodos de selección de tareas para Ingresar Gestión ===

  /**
   * Verifica si una tarea está seleccionada.
   */
  isTareaSelected(tarea: TareaBeneficiario): boolean {
    return this.tareasSeleccionadas().some(
      (t) => t.nmconsecutivo === tarea.nmconsecutivo
    );
  }

  /**
   * Verifica si todas las tareas están seleccionadas.
   */
  isAllTareasSelected(): boolean {
    const tareas = this.tareasBeneficiario();
    const seleccionadas = this.tareasSeleccionadas();
    return tareas.length > 0 && tareas.length === seleccionadas.length;
  }

  /**
   * Maneja el cambio de selección de una tarea individual.
   */
  onTareaSelected(event: any, tarea: TareaBeneficiario): void {
    const seleccionadas = [...this.tareasSeleccionadas()];
    const index = seleccionadas.findIndex(
      (t) => t.nmconsecutivo === tarea.nmconsecutivo
    );

    if (event.checked) {
      if (index === -1) {
        seleccionadas.push(tarea);
      }
    } else {
      if (index !== -1) {
        seleccionadas.splice(index, 1);
      }
    }

    this.tareasSeleccionadas.set(seleccionadas);
  }

  /**
   * Maneja el cambio de selección de todas las tareas.
   */
  onSelectAllTareas(event: any): void {
    if (event.checked) {
      // Seleccionar todas
      this.tareasSeleccionadas.set([...this.tareasBeneficiario()]);
    } else {
      // Deseleccionar todas
      this.tareasSeleccionadas.set([]);
    }
  }

  /**
   * Abre el diálogo de Ingresar Gestión.
   * Valida que haya al menos una tarea seleccionada.
   */
  abrirIngresarGestion(): void {
    if (this.tareasSeleccionadas().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'Seleccione al menos una tarea para ingresar gestión'
      });
      return;
    }

    this.mostrarIngresarGestion.set(true);
  }

  /**
   * Cierra el diálogo de Ingresar Gestión.
   */
  cerrarIngresarGestion(): void {
    this.mostrarIngresarGestion.set(false);
  }

  /**
   * Maneja el resultado del guardado de gestión.
   * Limpia la selección y muestra mensaje de éxito.
   */
  onGestionGuardada(resultado: IngresarGestionResult): void {
    if (resultado.exito) {
      // Limpiar selección
      this.tareasSeleccionadas.set([]);

      this.messageService.add({
        severity: 'success',
        summary: 'Gestión Guardada',
        detail: `Se registró la gestión para ${resultado.ordenesGuardadas.length} orden(es)`
      });
    }
  }
}
